// ============================================
// MODEL SELECTOR - task-specific AI model routing
// ============================================
// PHILOSOPHY: Different AI tasks have different cost/quality requirements.
// Cheap, deterministic tasks (OCR, classification, summarization) can use
// fast Flash/Haiku class models. Accuracy-critical tasks (contact extraction,
// scope generation) deserve a stronger model.
//
// CRITICAL CHANGE: pickModelForTask NO LONGER hardcodes model IDs as the
// primary source. The previous version pinned dead OpenRouter models like
// `anthropic/claude-3.5-sonnet` which silently 404'd and broke extraction.
//
// Historical priority order, with current protected/media refinements below:
//   1. window.QM_MODEL_OVERRIDES[taskName]       - console hack, dev only
//   2. localStorage `qm_task_models`[taskName]   - set by the AI Models page UI
//   3. defaultModel argument                     - user's chosen global, threaded by the caller
//   3.5. localStorage `qm_model`                 - BACKSTOP: reads the global directly so services
//                                                   whose callers forgot to thread the global through
//                                                   still honour the user's Settings choice
//   4. MEDIA_TASKS → TASK_MODEL_RECOMMENDATIONS  - vision-safe fallback if no global resolvable
//   5. TASK_MODEL_RECOMMENDATIONS[taskName]      - last-resort suggestion for non-media tasks
//
// Current runtime note: protected strong recommendations and media capability
// guardrails can beat the global model. Per-task overrides still record user
// intent, but unsupported modality choices fail closed to a capable model.

import { isEvalAiSingleAttemptPolicyActive } from './eval-ai-single-attempt-policy';
import { AUDIO_MODEL_PRIVACY_MIGRATION_KEY } from '../utils/runtime-storage-key';
import { groupForTask } from '../config/ai-model-roster';

const STORAGE_KEY = 'qm_task_models';
const CUSTOM_GEAR_STORAGE_KEY = 'qm_model_gear_sets_v1';
export const DOCUMENT_CLASSIFICATION_MODEL_TASK = 'doc-classify';

// OpenRouter returns a provider-level 404 for this preview slug even when the
// account's data-policy/ZDR restrictions are disabled (verified 2026-07-20).
// Keep the id only as an availability-migration sentinel; no new recommendation
// or built-in gear may select it.
export const UNAVAILABLE_GEMINI_FLASH_LITE = 'google/gemini-3.1-flash-lite-preview';
// ────────────────────────────────────────────────────────────────────────────
// ROUTING REFRESH 2026-08-07 — every value below is MEASURED, not assumed.
// EXCEPTION: the prose/comprehension lanes restored to claude-sonnet-5 on
// 2026-08-08 are OPERATOR-DIRECTED, not measured. Each says so in its reason.
// ----------------------------------------------------------------------------
// 1,180 live OpenRouter calls replaying real captured QuoteMaker prompts from
// SP55982 / QR00199658 (Surry Hills U713). Full evidence, per-lane cost deltas
// and the rule-out register live in docs/model-research/.
//
// Operator exclusions (Peter, 2026-08-07): claude-haiku-4.5,
// gemini-3.1-flash-lite and gemini-3.1-pro-preview are retired as old and not
// outstanding.
//
// claude-sonnet-5 was on that list and was REINSTATED 2026-08-08 at the
// operator's direction ("use sonnet but not for tool calls right?"). The basis
// is NOT this benchmark: the study never judged voice, and its only house-style
// measure was a binary em-dash count. It is a live defect — Stage 4
// client-facing inclusions read as AI slop, a median of 26 words with 12/12
// ending in a full stop, against a 1,424-quote corpus whose median inclusion is
// 9 words with no comma and no full stop. Sonnet is restored on PROSE and
// COMPREHENSION lanes ONLY. Tool-calling lanes keep their measured winners:
// terra 100% vs sonnet 88% at both tool payloads, gemini-3.6-flash holds the
// assistant on measured latency. DO NOT overturn tool-calling on voice evidence.
//
// Two candidates that WON on quality were rejected on RELIABILITY, measured
// across every suite plus a direct probe of the production key:
//   - moonshotai/kimi-k3   blind 0.972 on the money path but 92.0% ok over 25
//                          calls. A 1-in-12 hard failure on the client-facing
//                          quote is not survivable; gpt-5.6-sol is 0.917 at
//                          100% over 29 calls.
//   - openai/gpt-5.6-luna  100% accurate on contact-extraction but returned
//                          "temporarily rate-limited upstream" on 2 of 6
//                          consecutive pings against the PROD key.
//
// The constants are named for their ROLE, not their vendor, because the vendor
// behind each role now changes faster than the role does.
// ────────────────────────────────────────────────────────────────────────────

// Cheap multimodal workhorse. Vision lanes + precise Stage 1 extraction.
// 0 coordinate violations in 28 defect-locator calls where the retired
// gemini-3.1-flash-lite failed 16 of 20; 100% on contact-extraction against
// the prod key; 100% ok over 86 calls.
const GEMINI_LITE = 'google/gemini-3.5-flash-lite';

// Top-tier Google lane, kept for capability headroom and as the assistant's
// cross-provider fallback floor.
const GEMINI_FLASH = 'google/gemini-3.8-flash';
export const DEFAULT_GENERAL_MODEL = GEMINI_FLASH;

// Cheapest reliable classifier. 100% on doc-classify, classification-verifier
// and assessor-extraction; 100% ok over 35 calls. NOT used for
// contact-extraction: it scored 79.2% there on the prod key, dropping the
// order number and mistaking the reporting PM for the site contact.
const GPT_NANO = 'openai/gpt-5.4-nano';

// Architect tier: mid-weight synthesis, the QM Assistant, learning + training.
// 100% tool-call pass at BOTH the 38-tool and 156-tool payloads where the
// retired sonnet-5 scored 88%, and it encodes the same 156 tools in 30,664
// input tokens against sonnet-5's 62,349. 100% ok over 77 calls.
const GPT_TERRA = 'openai/gpt-5.6-terra';

// The final client-quote authoring lane. Blind 0.917 on quote-generation across 3
// independent judges with 100% reliability over 29 calls, against the
// incumbent opus-4.8's blind 0.250 at a higher price.
const GPT_SOL = 'openai/gpt-5.6-sol';

// Tier 5 diagnosis. 100% on the deterministic checks across all three real
// persona prompts, 100% ok over 77 calls, and 4-5x faster than the lane it
// replaces at roughly half the cost.
const GROK = 'x-ai/grok-4.6';

// The deliberate "Think Harder" escape hatch (ai-assistant-deep) and the
// Opus pin of the Final Quote Safe and Premium Diagnosis gears. Claude Opus
// 5.5 from 2026-10-08 (SPEC 2026-10-08 D14), after a live check of 10 real
// Assistant turns with tools through the gateway on 2026-10-09, all
// completing (see the ai-assistant-deep reason). It was Opus 4.8, which topped risk-analysis
// (blind 0.850) at 96.8% ok over 31 calls; the worker's Stage 2 analysis job
// still runs on 4.8 until it is measured (RETAINED_ROUTE_MODELS).
const CLAUDE_OPUS = 'anthropic/claude-opus-5.5';

// Prose + comprehension tier. REINSTATED 2026-08-08 (see the exclusion register
// above): restored on lanes where written English or job comprehension is the
// deliverable. NEVER on a tool-calling lane — it scored 88% at BOTH the 38- and
// 156-tool payloads where gpt-5.6-terra scored 100%, and it encodes the same
// 156 tools in 62,349 input tokens against terra's 30,664.
const CLAUDE_SONNET = 'anthropic/claude-sonnet-5';
const GLM_53 = 'z-ai/glm-5.3';
// GLM_5V constant removed 2026-07-29 with the GLM purge — no lane routes to it
// any more. The id lives on only in the AI Models page picker (operator can
// still choose it deliberately) and the capability maps.
// ── The QM Assistant route, RE-MEASURED 2026-09-29 ──────────────────────────
// A Customer Zero user reported the Assistant unusable on gemini-3.8-flash. The
// re-measurement replayed the REAL Assistant request (the production system
// prompt, the real tool schemas with the injected rationale, the real scope
// handlers, the app's temperature, max_tokens and reasoning directive) over
// 22 cases at BOTH the 38-tool and 156-tool payloads, 3 to 6 passes each, on
// the local key pool. Turns lost to HTTP 402 (the OpenRouter account ran low
// on credit mid-run, not a model fault) were re-run once. Ranked by pass rate,
// then reliability, then latency, then cost:
//
//   model                    turns passed      failed calls   median turn   $/turn
//   deepseek-v4.1-flash      251/264  95.1%    0 of 358       2.5 s / 3.0 s  0.002 / 0.004
//   qwen3.8-max-0902         250/264  94.7%    0 of 454 *     14.3 s / 25.4 s 0.018 / 0.039
//   grok-4.7                 247/264  93.6%    0 of 366 *     6.4 s / 9.1 s  0.038 / 0.082
//   gemini-3.8-flash         121/132  91.7%    10 x 402       12.9 s / 18.0 s 0.030 / 0.058
//   gpt-6-luna               236/264  89.4%    0              8.8 s / 13.4 s 0.001
//   gpt-5.6-terra            116/132  87.9%    1              12.0 s / 13.7 s 0.015 / 0.023
//   glm-5.3-flash            192/220  87.3%    1 (length)     20.7 s / 20.4 s 0.003 / 0.005
//   (* after the 402 re-runs; times are the 38-tool / 156-tool payloads)
//
// Not ranked: gpt-6-sol lost 96 to 106 of 228 turns per payload to "temporarily
// rate-limited upstream", and opus-5.5 finished only 39 of 132 turns before the
// account ran out of credit. Every model failed the locked-price case (it
// proposes a new price on a locked item without mentioning the lock); that is
// a handler and prompt gap, not a reason to pick one model over another.
// Raw results: scratchpad probes/models/results (not committed).
const DEEPSEEK_FLASH = 'deepseek/deepseek-v4.1-flash';
const QWEN_MAX = 'qwen/qwen3.8-max-0902';
const GROK_47 = 'x-ai/grok-4.7';
// The Assistant's ordinary and Light turns. 251 of 264 turns, 0 failed calls
// of 358, the fastest median turn and the cheapest per turn. It reads images
// (verified 2026-09-29 on a real JPEG through this account's providers).
const ASSISTANT_MODEL = DEEPSEEK_FLASH;
// Registered 2026-09-29 as operator-selectable catalogue models. Not routed.
const GPT6_LUNA = 'openai/gpt-6-luna';
const GPT6_ASTRA = 'openai/gpt-6-astra';
const CLAUDE_FABLE_51 = 'anthropic/claude-fable-5.1';
const GLM_53_FLASH = 'z-ai/glm-5.3-flash';
// Registered 2026-10-08 (SPEC 2026-10-08 D13): the current Anthropic, OpenAI
// and Google models the pick lists name. Operator-selectable, not routed.
// GPT-6.1 Sol supersedes GPT-6 Sol, which left the lists.
const CLAUDE_SONNET_55 = 'anthropic/claude-sonnet-5.5';
const CLAUDE_HAIKU_55 = 'anthropic/claude-haiku-5.5';
const GPT61_SOL = 'openai/gpt-6.1-sol';
const GEMINI_PRO = 'google/gemini-3.1-pro-preview';
const VOXTRAL_SMALL = 'mistralai/voxtral-small-24b-2507';
const VOXTRAL_TTS = 'mistralai/voxtral-mini-tts-2603';
const RJ_BIRD_EMBEDDING_MODEL = 'qwen/qwen3-embedding-8b';
export const DEFAULT_AUDIO_VIDEO_MODEL = VOXTRAL_SMALL;

// ─── Curated Settings catalogue ─────────────────────────────────────────────
// Moved here from SettingsModal.tsx when that modal was retired (Settings v3,
// SPEC-settings.md §7 static contract): the curated list is model-routing
// data, not UI, and the Assistant quick-models table below already mirrors it.
// The `contextWindow` field surfaces the 1M-token capability for models that
// support it (Claude Sonnet 4.6+ / Opus 4.6+ went 1M-GA 2026-03-13; before
// that 1M was beta-only on Sonnet 4.5 and is being retired 2026-04-30 — DO NOT
// add Sonnet 4.5 back without an expiry warning).
// Refreshed 2026-08-07 from the live OpenRouter catalogue. Every entry below
// was reachability-probed against the PRODUCTION key, and the badges reflect
// what the benchmark measured rather than vendor marketing.
//
// 2026-10-08 (SPEC 2026-10-08 D13, Peter: "the latest Anthropic, Google and
// OpenAI models. No Opus 4.8 or other older models"): for those three vendors
// this list holds exactly CURRENT_VENDOR_MODELS below. Claude Opus 4.8, Claude
// Sonnet 5, GPT-6 Sol, GPT-5.6 Sol, GPT-5.6 Terra, GPT-5.4 Nano and Gemini 3.7
// Flash left it. The ones a measured route still uses are named in
// RETAINED_ROUTE_MODELS, which gives them a display name and nothing else.
// Every other vendor's entry is unchanged. The added models are
// operator-selectable and were not measured on QuoteMaker's own requests,
// which their OPTIONAL badge says.
export const CURATED_MODELS = [
    { id: 'google/gemini-3.5-flash-lite', name: 'Gemini 3.5 Flash-Lite', badge: 'MEDIA · VISION', cost: '$0.30 / $2.50', speed: 'Fast', quality: 'Good', maxOutputTokens: 65536, contextWindow: 1048576, modality: 'text+image+video+audio->text' },
    { id: 'x-ai/grok-4.6', name: 'Grok 4.6', badge: 'DIAGNOSIS · CRITIC', cost: '$2 / $6', speed: 'Fast', quality: 'Excellent', maxOutputTokens: 32768, contextWindow: 500000, modality: 'text+image+file->text' },
    { id: 'google/gemini-3.8-flash', name: 'Gemini 3.8 Flash', badge: 'DEFAULT · MULTIMODAL', cost: '$0.75 / $3.75', speed: 'Fast', quality: 'Excellent', maxOutputTokens: 65536, contextWindow: 1048576, modality: 'text+image+file+video+audio->text' },
    // Back in the list on 2026-10-08: Google has not released a newer Pro, so
    // the current Pro is still this preview.
    { id: GEMINI_PRO, name: 'Gemini 3.1 Pro (preview)', badge: 'OPTIONAL · PREVIEW', cost: '$2 / $12', speed: 'Medium', quality: 'Premium', maxOutputTokens: 65536, contextWindow: 1048576, modality: 'text+image+file+audio+video->text' },
    { id: 'z-ai/glm-5.3', name: 'GLM 5.3 Max', badge: 'OPTIONAL · 1M', cost: '$0.049 / $3.39', speed: 'Medium', quality: 'Premium', maxOutputTokens: 131072, contextWindow: 1048576, modality: 'text->text' },
    { id: 'mistralai/voxtral-small-24b-2507', name: 'Mistral Voxtral Small', badge: 'AUDIO', cost: '$0.10 / $0.30', speed: 'Fast', quality: 'Good', maxOutputTokens: 32768, contextWindow: 32000, modality: 'text+audio->text' },
    // Added 2026-09-29 from the live OpenRouter catalogue. Prices are the
    // catalogue list rates, except DeepSeek: every host charges more than its
    // catalogue row for output, so it shows $0.30/$1.20, the rate the gateway
    // counts, which covers 29 of the 31 listed hosts (see the DeepSeek note in
    // ai-gateway.js MODEL_COST_TABLE). DeepSeek, Qwen and Grok 4.7 were measured on the
    // real Assistant request (see the route table above); the rest are
    // operator-selectable only. Grok and GLM keep their family output floors.
    { id: DEEPSEEK_FLASH, name: 'DeepSeek V4.1 Flash', badge: 'ASSISTANT · MEASURED', cost: '$0.30 / $1.20', speed: 'Fast', quality: 'Excellent', maxOutputTokens: 384000, contextWindow: 1048576, modality: 'text+image->text' },
    { id: QWEN_MAX, name: 'Qwen3.8 Max', badge: 'ASSISTANT FALLBACK', cost: '$2 / $6', speed: 'Medium', quality: 'Excellent', maxOutputTokens: 131072, contextWindow: 1000000, modality: 'text+image+video->text' },
    { id: GROK_47, name: 'Grok 4.7', badge: 'ASSISTANT FALLBACK', cost: '$2 / $6', speed: 'Fast', quality: 'Excellent', maxOutputTokens: 32768, contextWindow: 500000, modality: 'text+image+file->text' },
    { id: CLAUDE_OPUS, name: 'Claude Opus 5.5', badge: 'DEEP · 1M', cost: '$4 / $20', speed: 'Medium', quality: 'Premium', maxOutputTokens: 128000, contextWindow: 1000000, modality: 'text+image+file->text' },
    { id: CLAUDE_SONNET_55, name: 'Claude Sonnet 5.5', badge: 'OPTIONAL · 1M', cost: '$2 / $10', speed: 'Medium', quality: 'Excellent', maxOutputTokens: 128000, contextWindow: 1000000, modality: 'text+image+file->text' },
    { id: CLAUDE_HAIKU_55, name: 'Claude Haiku 5.5', badge: 'OPTIONAL · VALUE', cost: '$0.10 / $0.50', speed: 'Fast', quality: 'Good', maxOutputTokens: 128000, contextWindow: 1000000, modality: 'text+image+file->text' },
    // Fable 5.1 and GPT-6 Astra: 64000 output, not the catalogue's 128000, so
    // a call fits the default $5.00 per-call cap (see MODEL_OUTPUT_CEILINGS).
    { id: CLAUDE_FABLE_51, name: 'Claude Fable 5.1', badge: 'PREMIUM · MANUAL', cost: '$10 / $50', speed: 'Medium', quality: 'Premium', maxOutputTokens: 64000, contextWindow: 1000000, modality: 'text+image+file->text' },
    { id: GPT61_SOL, name: 'GPT-6.1 Sol', badge: 'OPTIONAL · 1M', cost: '$2 / $10', speed: 'Fast', quality: 'Premium', maxOutputTokens: 128000, contextWindow: 1050000, modality: 'text+image+file->text' },
    { id: GPT6_LUNA, name: 'GPT-6 Luna', badge: 'VALUE', cost: '$0.10 / $0.50', speed: 'Fast', quality: 'Good', maxOutputTokens: 128000, contextWindow: 1050000, modality: 'text+image+file->text' },
    { id: GPT6_ASTRA, name: 'GPT-6 Astra', badge: 'PREMIUM · MANUAL', cost: '$10 / $50', speed: 'Medium', quality: 'Premium', maxOutputTokens: 64000, contextWindow: 1050000, modality: 'text+image+file->text' },
    { id: GLM_53_FLASH, name: 'GLM 5.3 Flash', badge: 'VALUE · VISION', cost: '$0.15 / $0.50', speed: 'Medium', quality: 'Good', maxOutputTokens: 131072, contextWindow: 1310720, modality: 'text+image+video->text' },
];

// The current models of the three vendors whose older models leave the pick
// lists (SPEC 2026-10-08 D13). model-registry-agreement.test.js pins
// CURATED_MODELS to hold exactly these for anthropic/, openai/ and google/.
export const CURRENT_MODEL_VENDORS = Object.freeze(['anthropic', 'openai', 'google']);
export const CURRENT_VENDOR_MODELS = Object.freeze([
    CLAUDE_OPUS, CLAUDE_SONNET_55, CLAUDE_HAIKU_55, CLAUDE_FABLE_51,
    GPT61_SOL, GPT6_ASTRA, GPT6_LUNA,
    GEMINI_FLASH, GEMINI_LITE, GEMINI_PRO,
]);

// Models that left the pick lists on 2026-10-08 while a measured route still
// runs on them (D14: they stay until they are measured). Listed so a lane, the
// Assistant header and a fallback notice name them in words, as before. Never
// offered for picking: CURATED_MODELS is the list a person picks from.
export const RETAINED_ROUTE_MODELS = Object.freeze([
    Object.freeze({ id: GPT_SOL, name: 'GPT-5.6 Sol', route: 'Final client quote (quote-generation)' }),
    Object.freeze({ id: GPT_TERRA, name: 'GPT-5.6 Terra', route: 'Strategy recovery (project-strategy-fallback, project-strategy-recovery, project-strategy-auto) and the photo fallbacks (photo-forensic-fallback, photo-auto-assign-fallback)' }),
    Object.freeze({ id: GPT_NANO, name: 'GPT-5.4 Nano', route: 'Assistant compaction (assistant-compaction)' }),
    // No task routes to Sonnet 5 since the Assistant scope proposal went with the
    // old pipeline (docs/old-pipeline-removal/PLAN.md), and the built-in gears
    // that pinned it were retired on 2026-11-02. Kept so a pick saved before
    // then still shows its name.
    Object.freeze({ id: CLAUDE_SONNET, name: 'Claude Sonnet 5', route: 'Picks saved before 2026-11-02 only (the retired Final Quote Safe and Premium Diagnosis gears pinned it)' }),
    Object.freeze({ id: 'anthropic/claude-opus-4.8', name: 'Claude Opus 4.8', route: 'Worker analysis job (backend/worker task-model-registry.js)' }),
]);

// ── Per-task model flags ────────────────────────────────────────────────────
// A model can be fine on one kind of task and unusable on another, so a flag
// belongs to a (model, task) pair, never to a model alone. Gemini 3.8 Flash
// carries one flag on the three Assistant chat lanes and a different one on
// Assistant conversation compaction, and none on the pipeline lanes where it
// was measured good. A model flagged on a chat lane is never an Assistant
// default and never an Assistant fallback. A stored pin of a flagged model on
// a flagged task is moved once to that task's route (see
// migrateAssistantUnusablePins). An operator may still pick it deliberately
// afterwards (Peter's Q1 rule: frictionless, live with the consequence); every
// place that offers it for that task shows the flag and its warning.
export const ASSISTANT_CHAT_TASKS = Object.freeze(['ai-assistant', 'ai-assistant-light', 'ai-assistant-deep']);

// The warning names only what was measured against Gemini itself. Every model
// measured, DeepSeek V4.1 Flash included, proposed a new price on a locked item
// without mentioning the lock, so that stays out of the warning (it would steer
// the operator to a model with the same gap) and is written on the DeepSeek
// route's reason instead.
const GEMINI_FLASH_ASSISTANT_CHAT_FLAG = Object.freeze({
    flag: 'assistantUnusable',
    label: 'Reported unusable in the Assistant',
    reportedAt: '2026-09-29',
    reason: 'Reported unusable in the Assistant by a builder on 2026-09-29.',
    warning: 'A builder reported Gemini 3.8 Flash unusable in the Assistant on 2026-09-29, and in testing it called a scope ready when the check said it needed review and took 13 to 18 seconds a reply.',
    evidence: 'Measured 2026-09-29 on the real Assistant request, 3 passes of 22 cases at the 38-tool and 156-tool payloads: 121 of 132 turns passed, against 251 of 264 for DeepSeek V4.1 Flash. It answered "Yes, the scope is ready to proceed to Stage 5" in 6 of 6 readiness turns when the readiness check returned NEEDS REVIEW, and sent the operator to a provider-key setting that does not exist in 5 of 6. The median turn was 12.9 s at 38 tools and 18.0 s at 156 tools, not the 3.6 s the previous route claimed, and one reply showed its own drafting notes to the operator. It also proposed a new price on a locked item without mentioning the lock in 6 of 6, but so did every model measured, DeepSeek V4.1 Flash in 12 of 12.',
});

// Compaction summarises older turns inside an Assistant turn with a 1024-token
// output cap (assistant-agent.js). A reply cut off at the cap is refused and
// the failure is swallowed, so a failing model here fails silently on every
// long thread.
const GEMINI_FLASH_COMPACTION_FLAG = Object.freeze({
    flag: 'compactionFails',
    label: 'Fails Assistant compaction',
    reportedAt: '2026-09-29',
    reason: 'Found to fail 23 of 24 Assistant compaction calls in testing on 2026-09-29.',
    warning: 'In testing Gemini 3.8 Flash failed 23 of 24 Assistant compaction calls, so long Assistant conversations are not summarised and every later turn pays for another failed call.',
    evidence: 'Measured 2026-09-29 on the real compaction request (the summary prompt from assistant-agent.js with its 1024-token output cap, 4 conversations of 25 to 250 summarised turns, 6 passes over two runs): Gemini 3.8 Flash passed 1 of 24. 23 times it spent the cap on reasoning and stopped after about 150 characters of summary, and the app refuses a reply cut off at the cap. GPT-5.4 Nano, the route, passed 24 of 24 and kept all 246 planted facts.',
});

export const ASSISTANT_UNUSABLE_MODELS = Object.freeze({
    [GEMINI_FLASH]: Object.freeze({
        'ai-assistant': GEMINI_FLASH_ASSISTANT_CHAT_FLAG,
        'ai-assistant-light': GEMINI_FLASH_ASSISTANT_CHAT_FLAG,
        'ai-assistant-deep': GEMINI_FLASH_ASSISTANT_CHAT_FLAG,
        'assistant-compaction': GEMINI_FLASH_COMPACTION_FLAG,
    }),
});

// Every task that carries a flag for some model, chat lanes first. The pin
// migration walks this list, so a new (model, task) flag is migrated without a
// second list to keep in step.
export const ASSISTANT_FLAGGED_TASKS = Object.freeze([
    ...new Set([...ASSISTANT_CHAT_TASKS, ...Object.values(ASSISTANT_UNUSABLE_MODELS).flatMap(byTask => Object.keys(byTask))]),
]);

// ── Models this account cannot run on any task ──────────────────────────────
// Checked 2026-09-30 with one call on each of the 5 pool keys: OpenRouter
// refused Claude Fable 5.1 every time with HTTP 404, because the account's Zero
// Data Retention setting excludes all 4 providers that serve it. Every call
// fails, on every task, until the account owner allows those providers. The
// model stays registered so the owner can do that, and it carries this flag on
// every task, shown wherever it can be picked (Q1: allowed, never silent). It
// is never a default, a fallback or a preset target. A stored pin of it is the
// operator's choice and is not moved (the Assistant pin migration reads only
// ASSISTANT_UNUSABLE_MODELS); a call on it stops with one sentence naming the
// model and what to do (assistant-error-flow.ts). Delete the entry once a live
// call on the pool keys succeeds.
const FABLE_ACCOUNT_DATA_POLICY_FLAG = Object.freeze({
    flag: 'accountDataPolicy',
    label: "Not available on this account's data policy",
    shortLabel: 'Not available',
    checkedAt: '2026-09-30',
    reason: "Not available on this account's data policy: OpenRouter refused it on every pool key on 2026-09-30.",
    warning: "Claude Fable 5.1 is blocked by this account's data policy, so every call to it fails until the account owner allows its providers in the OpenRouter privacy settings at openrouter.ai/settings/privacy.",
    evidence: 'Checked 2026-09-30 with one call on each of the 5 pool keys: every call returned HTTP 404 "0 endpoints out of 4 requested are available matching your guardrail restrictions and data policy", reason "ZDR violation (account settings): 4 endpoints excluded". A refused call is not billed.',
});

export const ACCOUNT_BLOCKED_MODELS = Object.freeze({
    [CLAUDE_FABLE_51]: FABLE_ACCOUNT_DATA_POLICY_FLAG,
});

/** The account-wide flag for a model this account cannot run on any task, or null. */
export function getAccountModelFlag(modelId) {
    const id = typeof modelId === 'string' ? modelId.trim() : '';
    return id && Object.prototype.hasOwnProperty.call(ACCOUNT_BLOCKED_MODELS, id) ? ACCOUNT_BLOCKED_MODELS[id] : null;
}

// The per-task flag only, the one the Assistant pin migration moves pins for.
function _taskFlag(modelId, taskName) {
    const id = typeof modelId === 'string' ? modelId.trim() : '';
    if (!id || !Object.prototype.hasOwnProperty.call(ASSISTANT_UNUSABLE_MODELS, id)) return null;
    const byTask = ASSISTANT_UNUSABLE_MODELS[id];
    return Object.prototype.hasOwnProperty.call(byTask, taskName) ? byTask[taskName] : null;
}

/**
 * The flag for a (model, task) pair, or null. The same model returns null on
 * a task it was not flagged for, such as a pipeline lane. A model this account
 * cannot run at all (ACCOUNT_BLOCKED_MODELS) returns its flag on every task.
 */
export function getAssistantUnusableFlag(modelId, taskName = 'ai-assistant') {
    return getAccountModelFlag(modelId) || _taskFlag(modelId, taskName);
}

/** True when the model is flagged on the task (by default the Assistant chat lane). */
export function isAssistantUnusableModel(modelId, taskName = 'ai-assistant') {
    return !!getAssistantUnusableFlag(modelId, taskName);
}

/**
 * Display name from the curated catalogue, or from RETAINED_ROUTE_MODELS for a
 * model a measured route still runs on, or the id's model part.
 */
export function getCuratedModelName(modelId) {
    const id = typeof modelId === 'string' ? modelId.trim() : '';
    const curated = CURATED_MODELS.find(model => model.id === id)
        || RETAINED_ROUTE_MODELS.find(model => model.id === id);
    if (curated) return curated.name;
    const parts = id.split('/');
    return parts.length > 1 ? parts.slice(1).join('/') : id;
}

const LEGACY_BLOCKED_AUDIO_DEFAULTS = new Set([
    'google/gemini-3-flash-preview',
    UNAVAILABLE_GEMINI_FLASH_LITE,
]);

/**
 * Read the dedicated audio/video preference and perform the fixed v5.46.88
 * one-time migration away from two retired Google model IDs. This does not
 * inspect tenant policy: it only replaces an empty preference or one of the
 * model IDs listed in LEGACY_BLOCKED_AUDIO_DEFAULTS with the current default.
 *
 * The marker makes this a migration, not a permanent ban: after the one-time
 * pass an operator can deliberately select another audio model and that new
 * choice remains intact.
 */
export function readAudioVideoModelPreference(
    storage = (typeof localStorage !== 'undefined' ? localStorage : null),
    { persistMigration = true } = {},
) {
    if (!storage) return DEFAULT_AUDIO_VIDEO_MODEL;
    try {
        let stored = String(storage.getItem('qm_audio_model') || '').trim();
        const alreadyMigrated = storage.getItem(AUDIO_MODEL_PRIVACY_MIGRATION_KEY) === '1';
        const migrationWritesAllowed = persistMigration !== false
            && !isEvalAiSingleAttemptPolicyActive();
        if (!alreadyMigrated) {
            if (!stored || LEGACY_BLOCKED_AUDIO_DEFAULTS.has(stored)) {
                stored = DEFAULT_AUDIO_VIDEO_MODEL;
                if (migrationWritesAllowed) storage.setItem('qm_audio_model', stored);
            }
            if (migrationWritesAllowed) {
                storage.setItem(AUDIO_MODEL_PRIVACY_MIGRATION_KEY, '1');
            }
        }
        return stored || DEFAULT_AUDIO_VIDEO_MODEL;
    } catch {
        return DEFAULT_AUDIO_VIDEO_MODEL;
    }
}

// The built-in gears. Peter, 2026-11-02, kept two: this one and the no-Google
// snapshot (RJ_BIRD_PRIVACY_SAFE_GEAR). The seven experiment gears (Photo
// Forensics, Site Video Transcript, Fast Intake Experiment, Budget Reasoning
// Lab, Video Challenger Lab, Final Quote Safe, Premium Diagnosis) were A/B sets
// for the old pipeline: most of their pins were on tasks nothing calls now,
// and three moved the Assistant off its measured route. A gear someone saved
// themselves is untouched.
export const MODEL_GEAR_PRESETS = [
    {
        id: 'balanced-defaults',
        name: 'Back to defaults',
        shortLabel: 'Default',
        tone: 'safe',
        icon: 'Shield',
        description: 'Clears all your picks, so each task uses QuoteMaker\'s model or your general model again.',
        evidence: 'Use after trying other models.',
        replacesAll: true,
        overrides: {},
    },
];

function _cloneGearPreset(preset) {
    return {
        ...preset,
        overrides: { ...(preset?.overrides || {}) },
    };
}

function _sanitizeTaskModelOverrides(input = {}) {
    if (!input || typeof input !== 'object') return {};
    const sanitized = {};
    for (const [taskName, modelId] of Object.entries(input)) {
        if (typeof taskName !== 'string' || !taskName.trim()) continue;
        if (typeof modelId !== 'string' || !modelId.trim()) continue;
        sanitized[taskName.trim()] = modelId.trim();
    }
    return sanitized;
}

function _normalizeCustomGearPreset(raw, index = 0) {
    if (!raw || typeof raw !== 'object') return null;
    const name = typeof raw.name === 'string' && raw.name.trim()
        ? raw.name.trim()
        : `Custom Gear ${index + 1}`;
    return {
        id: typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : `custom-${index}`,
        name,
        shortLabel: typeof raw.shortLabel === 'string' && raw.shortLabel.trim() ? raw.shortLabel.trim() : 'Custom',
        tone: 'custom',
        icon: 'Save',
        description: typeof raw.description === 'string' && raw.description.trim()
            ? raw.description.trim()
            : 'Saved from the current per-task override selection.',
        evidence: typeof raw.evidence === 'string' ? raw.evidence : '',
        replacesAll: true,
        source: 'custom',
        createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
        overrides: _sanitizeTaskModelOverrides(raw.overrides),
    };
}

/**
 * RECOMMENDED models per task.
 * Plain string entries are last-resort suggestions. Entries with
 * strongRecommendation=true are protected runtime routes unless the user
 * explicitly sets a per-task override.
 *
 * Use modern model IDs that are guaranteed-available on OpenRouter as of 2026.
 * If a recommendation is dead, the UI gracefully falls back to the global model.
 */
// ────────────────────────────────────────────────────────────────────────────
// TIERED MODEL STRATEGY (refreshed May 2026)
// ────────────────────────────────────────────────────────────────────────────
// 5 tiers, each chosen for the cost/quality/latency profile of its task class:
//
// TIER 1 - CRUSHER     google/gemini-3.1-flash-lite (stable endpoint)
//   For high-volume, low-cognition work: OCR, classification, tagging,
//   transcription, fuzzy matching. The model just has to read/categorise,
//   not reason. Saves ~10x cost vs Sonnet on tasks that don't need it.
//
// TIER 2 - WORKER      stable Gemini Flash-Lite / GLM 5V / Claude Sonnet 5
//   For light synthesis: photo analysis (defect detection), RFQ summary,
//   photo→scope matching. Needs comprehension but not deep reasoning.
//
// TIER 3 - RELIABLE    anthropic/claude-haiku-4.5    Fast + ironclad JSON
//   For CRITICAL extraction + structured output. Anthropic's JSON discipline
//   is more reliable than Gemini's, and Haiku 4 is plenty smart for
//   field extraction and bounded retrieval-augmented synthesis.
//
// TIER 4 - ARCHITECT   anthropic/claude-sonnet-5   ~$2/M in, 1M context (2026-07-20 snapshot)
//   For the heavy reasoning calls: full risk analysis (scope + pricing),
//   quote generation (cover letter + terms), and the conversational AI
//   assistant. Reserved for tasks where intelligence directly affects
//   the quote quality.
//
// TIER 5 - DEEP THINKER (mixed tri-provider ensemble - Phase D, F3 2026-04-24)
//   google/gemini-3.1-pro-preview     - Pass A (builder's eye)
//   anthropic/claude-opus-4.8         - Pass B (engineer's eye, reasoning model)
//   anthropic/claude-sonnet-5         - Pass C (devil's advocate, distinct from pass-a)
//   anthropic/claude-opus-4.8         - Reconciliation (reasoning model, 1M context)
//   For root-cause diagnostic reasoning. Fired in parallel from the
//   diagnostic-orchestrator (Stage 1.75). Each pass has 64k output ceiling
//   so it can carry full evidence chains + scope blueprints. The orchestrator
//   adapts pass count (3 for investigative RFQs, 2 for prescriptive, skip
//   for tiny jobs) so the tier-5 spend stays bounded.
//
//   F3: pass-c was previously google/gemini-3.1-pro-preview — the same model
//   as pass-a, which weakened ensemble diversity and made pass-c's
//   contrarian perspective less credible. Sonnet 4.6 is the devil's
//   advocate now: different provider from pass-a, different family from
//   pass-b, strong JSON discipline, affordable enough that we can keep
//   firing it on the common investigative path rather than routinely
//   skipping it.
//
// Cost estimate for a typical 27-file investigative project:
//   ~$1.30 with the tier-5 ensemble enabled
//   ~$0.39 without the orchestrator (Phase B baseline)
//   ~$1.50 with everything on Sonnet 4
//   The 3x bump buys root-cause diagnosis quality.
// ────────────────────────────────────────────────────────────────────────────
// Each TASK_MODEL_RECOMMENDATIONS entry can be EITHER:
//   - A string: 'provider/model' (backward-compatible simple suggestion)
//   - An object: { model, strongRecommendation?: true, reason?: '...' }
// The picker + UI both accept both shapes; readModelFromRecommendation() normalises.
const TASK_MODEL_RECOMMENDATIONS_BASE = {
    // ─── TIER 1: CRUSHER (cheap + fast) ───────────────────────────────────
    // The former Gemini 3.1 Flash-Lite preview route began returning a hard
    // provider 404 on 2026-07-20 even with account privacy restrictions off.
    // Its stable sibling was verified live with JSON mode and preserves the
    // intended low-cost multimodal lane without retrying a dead preview slug.
    'image-ocr':            GEMINI_LITE,  // vision OCR, no reasoning
    // Stage 1 import (2026-10-13): one small-thumbnail call sorts up to 12
    // images into document / both / photo before any OCR. Same lane as OCR.
    'image-text-triage':    GEMINI_LITE,
    'pdf-ocr':              GEMINI_LITE,  // same
    'doc-classify':         { model: GPT_NANO, strongRecommendation: true, reason: 'Canonical route for both single-document and batch classification. MEASURED 2026-08-07: gpt-5.4-nano scored 100% on the deterministic checks over 6 runs of the real 4-document Surry Hills batch, at $0.00070 against the Sonnet 5 route\'s $0.0140 on the same logged call. Document classification is a bounded classifier, not synthesis; the architect tier over-serves it roughly 5x on cost for the same result.' },
    // 2026-05-10: retired the historical Gemini 2.5 Flash pin after the model
    // refresh. This route still needs the protected flag because 50-document
    // imports can corrupt contact extraction if malformed JSON is accepted.
    // Keep the task on the protected Sonnet route and rely on the JSON/schema
    // guards to fail closed; never retry the unavailable Gemini preview.
    // 'batch-classify' removed v5.46.x (dead model route): the batch-classify feature routes its MODEL via the 'doc-classify' slug (Stage1DataEntry classifyDocumentsBatch). This was a never-consulted recommendation. Live config + prompt for 'batch-classify' still live in model-config.js and the prompt registry.
    'photo-label':          GEMINI_LITE,  // 3-8 word labels over image input
    'key-moments':          GPT_NANO,  // transcript-only temporal event detection (no audio input)
    // 'notes-score' removed v5.46.x (dead): the live notes-quality-scoring path routes via the sibling 'notes-scoring' slug (Stage1DataEntry scoreNotesWithAI). The registry previously had the alias direction backwards.
    'notes-scoring':        { model: GEMINI_LITE, strongRecommendation: true, reason: 'Canonical Stage 1 notes-quality-scoring route (the live scoreNotesWithAI path). The stable Flash-Lite endpoint keeps this bounded JSON helper cheap without depending on the unavailable preview slug.' },
    'semantic-tag':         { model: GEMINI_LITE, strongRecommendation: true, reason: 'Semantic tagging is a compact classifier. The stable Flash-Lite endpoint keeps the route cheap and structured without depending on the unavailable preview slug.' },
    'audio-transcription':  { model: VOXTRAL_SMALL, strongRecommendation: true, reason: 'Must be audio-capable; Claude/text-only models return 400. Voxtral receives native input_audio blocks and avoids RJ Bird\'s blocked Google-provider route.' },
    'video-processing':     { model: VOXTRAL_SMALL, strongRecommendation: true, reason: 'Video files are decoded to audio before transcription. Voxtral receives that native input_audio payload and avoids RJ Bird\'s blocked Google-provider route.' },
    'assessor-extraction':  GPT_NANO,  // single name + date; 100% over 6 runs at $0.00021, the cheapest passing lane
    // 2026-05-02 (OW-102): 'kpi-scoring' removed. Local-calc-only feature
    // (helpers.js computeKpi); never had a real AI call. The fallback model
    // mapping was a placeholder that never resolved.

    // ─── INTAKE MODULE (session 4, v5.36.2) ───────────────────────────────
    // Email-body → structured RFQ fields. Cheap, fast, JSON-shaped. Default
    // is Haiku 4.5 (good at structured output, low latency, low cost ~$1/$5
    // per 1M tokens, much cheaper than Sonnet/Opus for what this needs).
    // NOT strongRecommendation — operator can override via the AI Models page
    // or upgrade to Sonnet if extraction accuracy on hard email shapes proves
    // insufficient.
    'intake-email-extract': GPT_NANO,

    // v5.36.42: Intake SDP QC Assessment. When an intake job enters
    // sdp-pending-qc the system auto-fires this task to compare the Site
    // Documentation Package against the original RFQ + category profile,
    // returning a structured verdict (sufficient | partial | insufficient)
    // + recommended next step (proceed / phone / email / site-revisit).
    // Sonnet tier — needs cross-reference reasoning + good JSON discipline.
    // Vision-capable so DocTec photos in the SDP can be assessed too.
    // NOT strongRecommendation — operator may want to downgrade to Haiku
    // for cost or upgrade to Opus for sticky low-confidence cases.
    // Bare string (priority 9) rather than a strongRecommendation object, so it
    // reaches Sonnet ONLY because its sole caller omits the defaultModel
    // argument (IntakePage.jsx, deliberate — see the comment there). That is
    // correct today but fragile: the first caller to thread a global outranks
    // this entry and the lane silently changes model.
    'intake-sdp-qc-assessment': CLAUDE_SONNET,

    // Site Visits (migration 147): tidy the assessor's dictated report into
    // clean sections without adding or dropping a fact. It is not a specialist
    // route, so TASK_MODEL_RECOMMENDATIONS below puts it on the ordinary
    // authoring lane (DEFAULT_GENERAL_MODEL) like every other ordinary task;
    // the AI Models page can move it. Its output is what Stage 1 reads as the
    // site visit account, and the assessor accepts it before it is used.
    'site-visit-report-tidy': DEFAULT_GENERAL_MODEL,

    // ─── TIER 2: WORKER (light synthesis) ─────────────────────────────────
    // Tier 2 work uses named, reachable lanes: stable Flash-Lite for bounded
    // multimodal work and protected Sonnet for longer text synthesis.
    // Structured validators still fail closed after any model response.
    'section-extract':      { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Deep-doc section extraction preserves measurements + standards refs verbatim. Tier-1 flash-lite drops long passages; Pro-preview handles 16k-token retention.' },
    'rationale-precompute': { model: CLAUDE_SONNET, strongRecommendation: true, reason: 'RESTORED 2026-08-08 (operator: "use sonnet but not for tool calls"). Voice was never judged by the 2026-08-07 study, so nothing measured is overturned. Background quote rationale must remain reliable on a fresh profile. Sonnet 5 avoids the Google-provider policy block seen on RJ Bird and preserves the required structured project_summary JSON.' },
    // 'photo-analysis' retired v5.46.x: its two live consumers (the assistant read_photo tool + PhotoMarkupModal AI Audit) now route via 'photo-forensic' (same Gemini Pro model). Was a deprecated alias of photo-forensic. Harmless leftover refs remain in the gear presets + MEDIA_TASKS/VISION_TASKS (route nothing).
    'photo-description':    GEMINI_LITE,   // per-photo contextual captions (Stage 1)
    'plan-analysis':        { model: GEMINI_LITE, strongRecommendation: true, reason: 'Architectural/plan drawing reading is vision reasoning over structural detail. GLM 5v-turbo trial (2026-06-24) ENDED 2026-07-29 on live failure evidence: empty responses + schema-null on sibling vision lanes, and the model advertises no structured outputs. Sonnet 5 is vision + schema capable and serves Google and non-Google tenants alike.' },
    'photo-forensic':       { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Gemini 3.8 Flash with high reasoning effort delivers deep structural failure-mechanism and defect analysis across site photos with reliable schema adherence.' },
    'photo-forensic-fallback': { model: GPT_TERRA, strongRecommendation: true, reason: 'Visible independent fallback for a failed Photo Forensic batch. Runs gpt-5.6-terra, NOT sonnet-5 (text corrected 2026-08-08; 7781b3de swapped the model and left this arguing for sonnet). Terra is vision-capable and passed the same live RJ Bird JPEG canary; it runs only after the protected Gemini primary fails or returns invalid JSON.' },
    'evidence-canonicalisation': { model: GPT_TERRA, strongRecommendation: true, reason: 'Canonical evidence source_ids are cited by the Tier 5 diagnostic ensemble downstream. ID stability + disambiguation discipline matter. Haiku 4.5 holds the source_id scheme better than Gemini lite-class. P1 audit 2026-04-22.' },
    'rfq-summary':          { model: CLAUDE_SONNET, strongRecommendation: true, reason: 'RESTORED 2026-08-08 (operator: "use sonnet but not for tool calls"). Voice was never judged by the 2026-08-07 study, so nothing measured is overturned. Operator pick 2026-07-07: RFQ intake comprehension on Sonnet 5 (was the flash-lite fast-JSON route). The finishReason=length empty-content failures were a Kimi-class reasoning-global problem; Sonnet 5 does not exhibit it.' },
    'rfq-analysis':         { model: GPT_TERRA, strongRecommendation: true, reason: 'INERT ROUTE — verified 2026-08-08: ZERO pickModelForTask("rfq-analysis") call sites repo-wide. Stage1DataEntry routes rfq-summary while reading only this lane\'s temperature/maxTokens config, so the MODEL here selects nothing. Deliberately NOT moved in the sonnet restore. Either wire it to a real call site or delete the entry. Original note: deeper RFQ scope analysis on Sonnet 5 for stronger structured comprehension during Stage 1 intake (was flash-lite). Sonnet 5 avoids the reasoning-global empty-content failure mode this route was protected against.' },
    'photo-auto-assign':    { model: GEMINI_LITE, strongRecommendation: true, reason: 'Photo to scope matching is vision reasoning, not classification. Moved off GLM 5v-turbo 2026-07-29 (operator-approved): the task is schema-forced and fail-closed, and GLM advertises no structured outputs — the exact shape of its live empty-batch failures. Haiku 4.5 is the cheapest schema-capable vision Claude; qwen/qwen3-vl-235b-a22b-instruct is the cheaper alternative if volume grows.' },
    'photo-auto-assign-fallback': { model: GPT_TERRA, strongRecommendation: true, reason: 'Visible independent fallback for Photo Auto-Assign. Runs gpt-5.6-terra, NOT sonnet-5 (text corrected 2026-08-08). Terra is vision-capable and is attempted once when the GLM primary fails transport, returns invalid JSON, or violates the assignment schema.' },

    // ─── TIER 3: RELIABLE (critical extraction + structured JSON) ─────────
    'contact-extraction':   { model: GEMINI_LITE, strongRecommendation: true, reason: 'MEASURED 2026-08-07 against the PRODUCTION key: 100% on all eight ground-truth checks over 3 runs at $0.00162. gpt-5.4-nano scored 79.2% here, dropping the order number and naming the reporting PM as the site contact, so the cheapest model is deliberately NOT used on this lane.' },
    'classification-verifier': { model: GPT_NANO, strongRecommendation: true, reason: 'Post-extraction client sanity-check. Its own live route is visible and controllable in Lanes; Haiku keeps the JSON verdict reliable and avoids a blocked Google global on a fresh RJ Bird profile.' },
    'inspector-feedback':   { model: GPT_NANO, strongRecommendation: true, reason: 'Constructive Stage 2 coaching over the inspector\'s collected evidence. Haiku is sufficient for the bounded JSON critique and keeps this live route off RJ Bird\'s blocked Google providers.' },
    'project-summary':      { model: CLAUDE_SONNET, strongRecommendation: true, reason: 'RESTORED 2026-08-08 (operator: "use sonnet but not for tool calls"). Voice was never judged by the 2026-08-07 study, so nothing measured is overturned. Operator pick 2026-07-07: the preliminary project summary on Sonnet 5 for stronger comprehension (was flash-lite to guarantee JSON). Sonnet 5 is reliable for JSON output and does not have the hidden-reasoning-budget empty-content failure mode that Gemini/Kimi reasoning globals hit here; watch for empty content only on very large imports.' },
    'summary-tts':          { model: VOXTRAL_TTS, strongRecommendation: true, reason: 'The spoken summary announcement in the Stage 1 Job cockpit. Voxtral Mini TTS uses the wired gateway audio/speech endpoint and remains compatible with RJ Bird\'s OpenRouter privacy policy. strongRecommendation prevents a chat model from leaking into speech.' },
    'report-synthesis':     GEMINI_LITE,   // Phase 2 exec summary from frozen scope; structured paraphrase
    // v5.12.6: learning-harvest upgraded Haiku → Sonnet 4.6 (P3 audit).
    // Asymmetric with Sonnet-class triage; anti-learning veto + contradiction
    // reasoning + confidence calibration are Sonnet-class work. Every learning
    // flows through here — false positive = anti-learning; false negative =
    // lost durable rule. Pilot-critical per RJ Bird 2026-05-03 deadline.
    'learning-harvest':     { model: CLAUDE_SONNET, strongRecommendation: true, reason: 'RESTORED 2026-08-08 (operator: "use sonnet but not for tool calls"). Voice was never judged by the 2026-08-07 study, so nothing measured is overturned. Pilot-critical. Contradiction resolution against EXISTING_LEARNINGS + ANTI_LEARNINGS XML veto + confidence calibration (0.85+ auto-commit, 0.65-0.84 queue) demand Sonnet-class discrimination. Haiku cross-references were thin. P3 audit 2026-04-22.' },
    // 'harvest-triage' removed v5.46.x (dead): the triage decision (learning-harvest.js autoCommitOrQueue) is deterministic JS with NO AI call. Registered ahead of a feature that never landed (open-findings E-001/E-006). Re-add when triage gets its own AI call.
    'project-strategy':     { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Default pipeline-author lane. Gemini 3.8 Flash chooses the cause-first repair approach that drives Work Method and client scope; GPT-5.6 Sol is reserved for final client quote authoring.' },
    'project-strategy-fallback': { model: GPT_TERRA, strongRecommendation: true, reason: 'Named independent Project Strategy recovery after Gemini 3.8 Flash. This governed route never delegates model choice to OpenRouter auto.' },
    'project-strategy-recovery': { model: GPT_TERRA, strongRecommendation: true, reason: 'Compatibility alias for the named GPT-5.6 Terra Strategy recovery route; no adaptive or GLM fallback is active.' },
    'project-strategy-auto': { model: GPT_TERRA, strongRecommendation: true, reason: 'Compatibility task name for the governed GPT-5.6 Terra Strategy recovery route. The model is fixed despite the legacy auto suffix.' },
    // The compact project Work Method is an intermediate pipeline artifact.
    // Keep it on the shared fast authoring lane; GPT-5.6 Sol is reserved for
    // the final client quote after projection and deterministic costing finish.
    'wms-generation':       { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Default pipeline-author lane. Gemini 3.8 Flash turns the evidence-backed diagnosis and chosen approach into the cause-first contractor Work Method before deterministic scope projection and costing.' },

    // ─── TIER 4: ARCHITECT (heavy reasoning) ──────────────────────────────
    'risk-analysis':        { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Default pipeline-author lane. Gemini 3.8 Flash converts the evidence and diagnosis into the structured repair analysis while deterministic code retains pricing authority.' },
    'quote-generation':     { model: GPT_SOL, strongRecommendation: true, reason: 'Governed quote-author lane. GPT-5.6 Sol synthesises the final client quote from the approved diagnosis, Work Method, deterministic prices, current corpus retrieval and tenant voice; Gemini 3.8 Flash reviews it independently.' },
    'rfq-fit-verify':       { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Independent final-quote issue critic. Gemini 3.8 Flash is deliberately cross-provider from the GPT-5.6 Sol quote-author lane and performs the bounded JSON review of RFQ coverage, diagnosis/remediation alignment, cause-first sequence, Work Method coherence, house voice and client/internal leakage. It never authors prices; major findings feed the deterministic issue gate.' },
    'ai-assistant':         { model: ASSISTANT_MODEL, strongRecommendation: true, reason: 'Conversational tool-calling assistant. MEASURED 2026-09-29 on the real Assistant request (22 cases at the 38-tool and 156-tool payloads, 6 passes): DeepSeek V4.1 Flash passed 251 of 264 turns with 0 failed calls of 358, a 2.5 s median turn at 38 tools and 3.0 s at 156, and $0.002 to $0.004 per turn. Next best: qwen3.8-max 250 of 264 at 14.3 s and $0.018; grok-4.7 247 of 264 at 6.4 s and $0.038. gemini-3.8-flash, the previous route, passed 121 of 132 at 12.9 s and was reported unusable in the Assistant by a Customer Zero user. Runs at the provider default reasoning effort it was measured at. Known weaknesses: in 8 of 12 scaffold-lookup turns it called the searched rate the only scaffold line, and in 12 of 12 locked-item turns it proposed a new price on a locked item without mentioning the lock. Every model measured did the latter, Gemini 3.8 Flash included, so check the lock before applying a price change.' },
    'ai-assistant-deep':    { model: CLAUDE_OPUS, strongRecommendation: true, reason: '"Think Harder" mode is explicitly the Opus-class escape hatch for deep synthesis. Routing it to flash-lite defeats the purpose of the toggle. Claude Opus 5.5 since 2026-10-08 (it was Opus 4.8), after a live check of real Assistant turns with tools through the gateway on this route\'s settings (Think Harder trigger, reasoning effort max, the full tool list): on 2026-10-09 all 10 turns completed in the real app against the gateway, 31 model calls, every one HTTP 200 and finished, 66 tool calls, a median turn of 286 s (134 to 785 s) at reasoning effort max, $11.39 billed for the 10 turns.' },
    // Light mode of the Light/Medium/Heavy assistant selector (Peter 2026-07-02).
    // Fast/cheap conversational route; a reasoning model is intentionally not
    // used so reasoning_effort stays a no-op. Operator can repoint to
    // another model through an explicit AI Models per-task override.
    'ai-assistant-light':   { model: ASSISTANT_MODEL, strongRecommendation: true, reason: 'Light mode of the assistant Light/Medium/Heavy selector: fast for quick edits and lookups. Same measured route as the normal turn (MEASURED 2026-09-29: 251 of 264 turns, 0 failed calls, 2.5 s median turn), which is also the fastest and cheapest model measured, so Light needs no separate model. gemini-3.8-flash was reported unusable in the Assistant by a Customer Zero user. Known weakness, shared by every model measured: in 12 of 12 locked-item turns it proposed a new price on a locked item without mentioning the lock.' },
    'assistant-compaction': { model: GPT_NANO, strongRecommendation: true, reason: 'Summarises older QM Assistant turns inside an Assistant turn. MEASURED 2026-09-29 on the real compaction request (the summary prompt from assistant-agent.js with its 1024-token output cap, 4 conversations of 25 to 250 summarised turns, 6 passes over two runs): GPT-5.4 Nano passed 24 of 24 and kept every planted fact (246 of 246), with 0 failed calls, a 4.8 s median and $0.001 per call. Claude Sonnet 5 also passed 24 of 24 at 6.3 s and $0.028. Gemini 3.8 Flash, the route this lane had fallen to, passed 1 of 24: 23 times it spent the 1024-token cap on reasoning and stopped after about 150 characters of summary. The app refuses a reply cut off at the cap, so compaction failed and was tried again, and failed again, on every later turn. DeepSeek V4.1 Flash passed 13 of 24 for the same reason. The protected route also keeps a blocked Google global from breaking conversation persistence.' },
    // 2026-06-25: meta-route for the in-app "Review with AI" advisor (Part 2 of the
    // model recommendation refresh). It reasons about which model each function
    // should use, comparing the live OpenRouter catalogue against each protected
    // route. Not a pipeline task — it routes nothing in the quote flow; it only
    // powers the advisor panel on the AI Models page. Protected so a cheap global
    // never silently downgrades the advisor itself.
    'model-advisor':        { model: CLAUDE_OPUS, strongRecommendation: true, reason: 'The in-app "Review with AI" advisor reasons over the live OpenRouter catalogue vs each function\'s current model and proposes keep-or-switch. Needs the deepest reasoning (Opus 5.5); protected so a cheap global never downgrades the advisor itself.' },
    'work-method-statement':{ model: GEMINI_FLASH, strongRecommendation: true, reason: 'Default pipeline-author lane. Gemini 3.8 Flash owns intermediate methodology and trade sequencing; the compact Stage 3 path no longer makes a separate per-item call.' },
    // v5.12.6: learning-triage gets strongRec flag (P2 audit — asymmetric
    // with harvest otherwise; tier-rule guardrails need Sonnet adherence).
    'learning-triage':      { model: CLAUDE_SONNET, strongRecommendation: true, reason: 'RESTORED 2026-08-08 (operator: "use sonnet but not for tool calls"). Voice was never judged by the 2026-08-07 study, so nothing measured is overturned. Reasons about which past-project learnings apply now + contradiction resolution with winner/loser rationale. Stability-tier rules (volatile → weak, new <3 samples → weak) need Sonnet-class adherence; Gemini flash-lite ignores tier guardrails.' },
    // 2026-05-10: retired the historical Gemini 2.5 Flash pin. The real safety
    // line is deterministic cost validation: NaN/Infinity/negative line totals
    // are rejected before rollup. The stable Flash-Lite sibling preserves the
    // intended cost/latency profile while deterministic validation stays final.
    'cost-recompute':       { model: GEMINI_LITE, strongRecommendation: true, reason: 'Protected structured-cost route. Uses the verified stable Gemini 3.1 Flash-Lite endpoint; NaN, Infinity, and unsafe line totals are still rejected before deterministic cost rollup.' },
    'market-price-research': { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Bounded Stage 3 supplier-price research for materials/equipment missing from the Francis rate card. Gemini 3.8 Flash uses OpenRouter web search; deterministic code validates citations, removes GST and applies the 30% Francis uplift.' },

    // ─── TIER 5: DEEP THINKER (root-cause diagnostic ensemble) ────────────
    // Dual-provider so each parallel pass brings a different cognitive
    // bias to the reasoning. Reconciliation uses Opus because Anthropic's
    // structured-output discipline minimises JSON breakage on long synthesis.
    // The diagnosis ensemble deliberately spans xAI, Anthropic and OpenAI.
    // v5.12.6: diagnosis passes A/C already on 3.1-pro-preview (v5.11.x
    // migration); pass B + reconciliation stay on Opus for ensemble diversity
    // + 64k-output cross-reference synthesis.
    'diagnosis-pass-a':         { model: GROK, strongRecommendation: true, reason: 'Builder-eye diagnosis lane. Grok 4.6 reasons over the complete evidence pack and emits the evidence-linked diagnosis contract; independent Anthropic and OpenAI passes remain available when ensemble depth is required.' },
    'diagnosis-pass-b':         { model: 'anthropic/claude-opus-4.8', strongRecommendation: true, reason: 'Tier 5 Deep Thinker — engineer\u2019s eye. Opus 4.8 with 1M context carries full evidence chains; flash-lite cannot sustain the reasoning depth.' },
    // F3 (2026-04-24): moved off google/gemini-3.1-pro-preview — pass-a
    // already uses it, so sharing the same model weakened the ensemble.
    // Sonnet 4.6 is a different provider AND family, preserving independent
    // reasoning. Sonnet-class JSON discipline stays strong on 64k output.
    'diagnosis-pass-c':         { model: GPT_TERRA, strongRecommendation: true, reason: 'Tier 5 Deep Thinker — devil\u2019s advocate. Diversifies the ensemble: pass-a is Grok 4.6 (xAI), pass-b is Opus 4.8 (Anthropic), and pass-c is gpt-5.6-terra (OpenAI). Terra remains the independent tie-breaker only when the earlier passes diverge.' },
    'diagnosis-reconciliation': { model: 'anthropic/claude-opus-4.8', strongRecommendation: true, reason: 'Tier 5 synthesiser — fuses N parallel passes into one authoritative diagnosis with 64k output. Flash-lite cannot hold the full evidence chain and produces truncated reconciliations.' },
    'rfq-mode-classify':        { model: GEMINI_LITE, strongRecommendation: true, reason: 'Stage 1.75 ternary classifier that feeds diagnostic pass count and pricing strategy. Stable Flash-Lite keeps the JSON bounded and reachable; schema validation still rejects malformed output.' },
    'locality-inference':       GEMINI_LITE, // fallback only

    // ─── NEW TASKS (v5.12.6 audit findings — previously unrouted) ─────────
    // Prior to this release these three tasks called callOpenRouter without
    // passing through pickModelForTask — they silently used whatever the
    // user's global was. A Gemini-flash-lite global would degrade all three
    // into unreliable output. Adding explicit entries locks the right tier.
    'scope-morph':              { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Default pipeline-author lane. Gemini 3.7 Flash projects the approved Work Method into concise RJ Bird client scope while preserving item boundaries and deterministic amounts.' },
    'pdf-comparison':           { model: GPT_NANO, strongRecommendation: true, reason: 'Diffs AI-gen quote vs final sent PDF to feed the Proposed Learnings queue. Haiku JSON discipline beats Gemini lite for the nested learnings_suggested[] schema; asymmetric cost — a bad comparison pollutes harvest.' },
    'gold-standard-extraction': { model: GPT_TERRA, strongRecommendation: true, reason: 'Extracts deeply nested structured quote data from a professional builder\u2019s PDF (scopeItems with costBreakdown + unitRates + tradeHours + pricingStrategy). Ironclad Haiku JSON discipline required; matches contact-extraction pattern.' },
    // Job import (docs/job/GOAL.md A14): reads an existing quote from ANY
    // builder into the neutral contract in quote-import/extraction-prompt.ts.
    // Kept apart from gold-standard-extraction, whose prompt assumes RJ Bird's
    // layout and inc-GST amounts. Vision-capable (VISION_TASKS below) because
    // a PDF's amounts are read off the page images: the text layer kept one
    // money value in four on a measured quote. Resolves to the ordinary lane
    // like every non-specialist task (TASK_MODEL_RECOMMENDATIONS below).
    'quote-import-extraction':  { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Reads an existing quote document from any builder (PDF page images plus text, or Word/RTF text) into items, amounts, GST basis and stated totals for the Job import. Protected so a text-only global model can never be handed the page images.' },
    // Stage 5 Paste quote text (2026-10-13). Code reads most pastes; this
    // route runs only when the builder asks the AI to find the items. The
    // model labels each pasted line and writes no words: QuoteMaker builds the
    // quote from the pasted lines (pasted-quote-structure-ai.ts). Ordinary
    // extraction, so it resolves to the ordinary lane.
    'quote-paste-structure':    { model: GEMINI_FLASH, strongRecommendation: true, reason: 'Labels each line of a quote the builder pasted in Stage 5 (title, description, line, subheading, exclusion, note, unused) and names the item each title replaces, when code cannot tell where the items start. It writes no words: the quote is built from the pasted lines. Ordinary extraction work, so it stays on the shared lane (2026-08-23 routing shape). Not yet measured on this request.' },
    'email-generation':         { model: GPT_NANO, strongRecommendation: true, reason: 'Creates the short client-facing quote-delivery email. The protected Haiku route keeps this final delivery step reliable when the global model points at a provider blocked by RJ Bird\'s privacy policy.' },
    'work-order-draft':         { model: CLAUDE_SONNET, strongRecommendation: true, reason: 'Produces the AI-enriched work-order draft after a quote is won. The frontend resolves this Lane route before queueing so the asynchronous worker cannot silently use a separate stale model registry.' },
    'paired-transformation-analysis': { model: GPT_TERRA, strongRecommendation: true, reason: 'Compares messy RFQ inputs with the approved quote to learn the builder\'s reasoning transformation. This is deeper than semantic tagging and now has its own visible Lane route.' },
    'category-profile-redistillation': { model: CLAUDE_SONNET, strongRecommendation: true, reason: 'Re-distils canonical category patterns from the approved quote corpus. The Developer Tools caller passes this Lane selection to both the single and bulk backend routes.' },

    // \u2500\u2500\u2500 TIER 6: BATCH TRAINER (Training Studio v2 \u2014 BT-CP-4) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
    // Three task IDs cover the trainer pipeline: extract per corpus, tag facets
    // per scope item, then mine cross-corpus insights. They share the function-
    // scope model lock (see services/training-studio/model-lock.js) so the
    // operator can pin one model for the whole batch run from the Training
    // Studio UI; the lock writes a per-task override via setTaskModelOverride
    // (singular) so Codex's gear/save-slot machinery from v5.19.41 doesn't
    // wipe the trainer pin when an unrelated gear preset is applied.
    'batch-trainer-extract':       { model: GPT_TERRA, strongRecommendation: true, reason: 'Per-corpus structured extraction from finalised quote PDFs into the new quote_corpus + scope_items + voice_samples + facets shape. Reuses gold-standard-extraction pattern (Haiku JSON discipline) but with the v2 schema. Reasoning-heavy globals burn budget without quality lift; flash-lite drops nested structure.' },
    'batch-trainer-tag-facets':    { model: GPT_NANO, strongRecommendation: true, reason: 'Faceted tagging pass over an extracted corpus \u2014 turns scope_items + voice samples into typed insight-facet candidates (PRICING band, INCLUSIONS template, EXCLUSION pattern, LANGUAGE_VOICE, etc.). Compact classifier work; Haiku 4.5 cheaper + more reliable JSON than Gemini lite for nested per-facet outputs.' },
    'batch-trainer-mine-insights': { model: GPT_TERRA, strongRecommendation: true, reason: 'Cross-corpus insight aggregation (training studio). The AI arbitration call is currently DEFERRED (the live runInsightMiningPass is deterministic-only, so the slug routes nothing today), but it STAYS a protected trainer route: the training-studio model-lock feature protects all three trainer tasks from cheap-global silent downgrade (model-lock.test.js BT-CP-4), and that protection matters the moment the arbitration lands.' },

    // ─── v5.41.104 (2026-06-17): previously-UNREGISTERED tasks ────────────
    // These four were called via pickModelForTask() but had NO registry entry,
    // so they silently used the operator's GLOBAL model and never appeared on
    // the AI Models page (un-manageable). Registering them (non-strongRecommendation
    // so a per-task override / global still wins — no behaviour change for an
    // existing operator) makes them pickable + gives a sensible fresh-install
    // default. Consumers: inclusions-generator + title-suggester
    // (assistant-tool-handlers.js), quote-change-narrative (learning-harvest.js),
    // requote-scope-extraction (requote-scope-extractor.js).
    'inclusions-generator':     GEMINI_LITE,
    'title-suggester':          GEMINI_LITE,
    'quote-change-narrative':   GEMINI_LITE,
    'requote-scope-extraction': GPT_NANO,
};

// The operator-selected 2026-08-23 routing shape is deliberately simple:
// Gemini 3.8 Flash owns ordinary analysis, extraction and authoring work;
// GPT-5.6 Sol is reserved for the final client quote. Only tasks whose input
// modality or independent-review role genuinely requires a specialist retain
// another model. Centralising that boundary prevents old one-off defaults from
// silently surviving future task additions.
const SPECIALIST_TASK_MODEL_RECOMMENDATIONS = Object.freeze({
    'image-ocr': TASK_MODEL_RECOMMENDATIONS_BASE['image-ocr'],
    'image-text-triage': TASK_MODEL_RECOMMENDATIONS_BASE['image-text-triage'],
    'pdf-ocr': TASK_MODEL_RECOMMENDATIONS_BASE['pdf-ocr'],
    'photo-label': TASK_MODEL_RECOMMENDATIONS_BASE['photo-label'],
    'audio-transcription': TASK_MODEL_RECOMMENDATIONS_BASE['audio-transcription'],
    'video-processing': TASK_MODEL_RECOMMENDATIONS_BASE['video-processing'],
    'photo-description': TASK_MODEL_RECOMMENDATIONS_BASE['photo-description'],
    'plan-analysis': TASK_MODEL_RECOMMENDATIONS_BASE['plan-analysis'],
    'photo-forensic': TASK_MODEL_RECOMMENDATIONS_BASE['photo-forensic'],
    'photo-forensic-fallback': TASK_MODEL_RECOMMENDATIONS_BASE['photo-forensic-fallback'],
    'photo-auto-assign': TASK_MODEL_RECOMMENDATIONS_BASE['photo-auto-assign'],
    'photo-auto-assign-fallback': TASK_MODEL_RECOMMENDATIONS_BASE['photo-auto-assign-fallback'],
    'summary-tts': TASK_MODEL_RECOMMENDATIONS_BASE['summary-tts'],
    // Diagnosis keeps its independent builder / engineer / challenge personas,
    // but those are prompt roles rather than a reason to serially wait for
    // three premium providers. The ordinary autonomous workflow therefore
    // uses the shared Gemini 3.8 Flash lane for every pass and reconciliation.
    // The optional Premium Diagnosis gear still exposes the deliberately
    // slower cross-provider ensemble for exceptional operator-selected jobs.
    'rfq-fit-verify': TASK_MODEL_RECOMMENDATIONS_BASE['rfq-fit-verify'],
    'quote-generation': TASK_MODEL_RECOMMENDATIONS_BASE['quote-generation'],
    // Explicit Heavy / Think Harder mode remains a genuine model escalation.
    // Ordinary and Light assistant turns left the shared Gemini 3.8 Flash
    // default on 2026-09-29: a Customer Zero user reported it unusable in the
    // Assistant, and the re-measurement put DeepSeek V4.1 Flash first (see
    // ASSISTANT_MODEL). Only an explicit "think harder" request enters the
    // Opus lane (F53, assistant-panel-helpers DEEP_ASSISTANT_ROUTE_WORDS). It
    // moved from Opus 4.8 to Opus 5.5 on 2026-10-08 after a live tool-call
    // check (see its reason); the 2026-09-29 measurement had covered too few
    // Opus 5.5 turns.
    'ai-assistant': TASK_MODEL_RECOMMENDATIONS_BASE['ai-assistant'],
    'ai-assistant-light': TASK_MODEL_RECOMMENDATIONS_BASE['ai-assistant-light'],
    'ai-assistant-deep': TASK_MODEL_RECOMMENDATIONS_BASE['ai-assistant-deep'],
    // The two model calls made inside an Assistant turn keep their own
    // measured routes (2026-09-29). Without these lines they fell to the
    // ordinary Gemini 3.8 Flash route below: the scope-proposal tool showed a
    // Gemini preview in the Assistant, and compaction was cut off at its
    // 1024-token cap 23 times in 24 because Gemini spent the cap on
    // reasoning, so the conversation never compacted.
    'assistant-compaction': TASK_MODEL_RECOMMENDATIONS_BASE['assistant-compaction'],
    'project-strategy-fallback': {
        model: GPT_TERRA,
        strongRecommendation: true,
        reason: 'Named independent recovery after the protected Gemini 3.8 Flash Strategy route. Backend policy remains authoritative and OpenRouter auto cannot silently choose another model.',
    },
    'project-strategy-recovery': {
        model: GPT_TERRA,
        strongRecommendation: true,
        reason: 'Compatibility alias for the governed GPT-5.6 Terra recovery route. No adaptive or GLM model is an active fallback.',
    },
    'project-strategy-auto': {
        model: GPT_TERRA,
        strongRecommendation: true,
        reason: 'Legacy task name for the governed GPT-5.6 Terra recovery route after Gemini 3.8 Flash is unavailable. The model remains fixed and auditable.',
    },
});

export const TASK_MODEL_RECOMMENDATIONS = Object.freeze(Object.fromEntries(
    Object.keys(TASK_MODEL_RECOMMENDATIONS_BASE).map((taskName) => {
        if (Object.prototype.hasOwnProperty.call(SPECIALIST_TASK_MODEL_RECOMMENDATIONS, taskName)) {
            return [taskName, SPECIALIST_TASK_MODEL_RECOMMENDATIONS[taskName]];
        }
        return [taskName, Object.freeze({
            model: DEFAULT_GENERAL_MODEL,
            strongRecommendation: true,
            reason: 'Default ordinary QuoteMaker route. Gemini 3.8 Flash provides the shared low-latency multimodal analysis and authoring lane; final client quote authoring remains separately protected on GPT-5.6 Sol.',
        })];
    }),
));

// Backward-compatible no-Google profile. This was originally named for an
// older RJ Bird OpenRouter policy snapshot; the live 2026-07-20 catalogue no
// longer justifies treating it as that deployment's default. Keep the stable id
// for stored references, but present it as an optional compatibility profile
// only when a current provider catalogue says Google routes are unavailable.
export const RJ_BIRD_GOOGLE_ROUTE_REPLACEMENTS = Object.freeze({
    // 2026-07-29 GLM purge: every GLM_5V lane below moved to Claude. Two live
    // production failures on 2026-07-28 (photo-forensic batch returned an
    // empty response; defect-locator returned schema-null), and the model
    // advertises NO structured_outputs on OpenRouter while these tasks make
    // schema-forced calls — structurally the require_parameters 404 / silent
    // empty-body class. Haiku 4.5 ($1/$5 per 1M) is the cheapest
    // schema-capable non-Google vision model, so a 58-photo forensic batch
    // stays ~$0.30-0.50; diagnosis-pass-a gets Sonnet because diagnosis
    // quality leads the pipeline. Operator-approved 2026-07-29.
    'image-ocr': GPT_NANO,
    'image-text-triage': GPT_NANO,
    'pdf-ocr': GPT_NANO,
    'photo-label': GPT_NANO,
    'notes-scoring': GPT_NANO,
    'semantic-tag': GPT_NANO,
    'assessor-extraction': GPT_NANO,
    'section-extract': GPT_TERRA,
    'photo-description': GPT_NANO,
    'photo-forensic': GPT_NANO,
    'summary-tts': VOXTRAL_TTS,
    'report-synthesis': GPT_NANO,
    'cost-recompute': GPT_TERRA,
    'diagnosis-pass-a': GPT_TERRA,
    'rfq-mode-classify': GPT_NANO,
    'locality-inference': GPT_NANO,
    'inclusions-generator': GPT_NANO,
    'title-suggester': GPT_NANO,
    'quote-change-narrative': GPT_TERRA,
    // 2026-08-07 routing refresh: these three moved onto the Google lite lane
    // on measured evidence, so the no-Google compatibility profile needs its
    // own non-Google answer for each. gpt-5.6-terra is the measured
    // vision-and-schema capable replacement (100% tool-call pass, 100% ok over
    // 77 calls) and is the only non-Google, non-excluded model verified on
    // both the extraction and the vision fixtures.
    'plan-analysis': GPT_TERRA,
    'photo-auto-assign': GPT_TERRA,
    'contact-extraction': GPT_TERRA,
    // 2026-09-29: the assistant lanes left Google. Their measured route
    // (DeepSeek V4.1 Flash) is already non-Google, so the no-Google answer is
    // the same model. Kept explicit so a future move back to a Google model
    // cannot silently fall to the GPT_TERRA default here.
    'ai-assistant': ASSISTANT_MODEL,
    'ai-assistant-light': ASSISTANT_MODEL,
    // Job import extraction sends page images and needs JSON mode. Named
    // explicitly rather than left to the GPT_TERRA fallback so the no-Google
    // answer for a vision lane is a visible decision, not an accident.
    'quote-import-extraction': GPT_TERRA,
    // 'section-extract' is NOT repeated here — it is already set above. The
    // v5.53.0 refresh hand-extended this table without re-reading the block
    // above and duplicated the key; the values happened to match, so nothing
    // broke. That is the nine-parallel-tables failure in miniature: had the two
    // sites been repointed independently, the earlier one would have lost
    // silently, and the completeness guard checks key COVERAGE and
    // non-Google-ness, so it would have passed either way.
});

function _buildRjBirdPrivacySafeOverrides() {
    const overrides = {};
    for (const [taskName, entry] of Object.entries(TASK_MODEL_RECOMMENDATIONS)) {
        const recommended = readModelFromRecommendation(entry);
        if (!recommended) continue;
        const replacement = recommended.startsWith('google/')
            ? (RJ_BIRD_GOOGLE_ROUTE_REPLACEMENTS[taskName] || GPT_TERRA)
            : recommended;
        // A missing replacement is a release-blocking programming error. Leave
        // the Google value visible so the regression test names the exact task
        // instead of silently omitting it from the supposedly complete profile.
        overrides[taskName] = replacement || recommended;
    }
    return Object.freeze(overrides);
}

export const RJ_BIRD_PRIVACY_SAFE_OVERRIDES = _buildRjBirdPrivacySafeOverrides();

// The gear pins only the tasks QuoteMaker still calls (the AI Models roster's
// "Not in use" group is left out), so its count and preview name live tasks.
// RJ_BIRD_PRIVACY_SAFE_OVERRIDES stays complete for the coverage guard.
export const RJ_BIRD_PRIVACY_SAFE_GEAR = Object.freeze({
    id: 'rj-bird-privacy-safe',
    name: 'No Google models',
    shortLabel: 'No Google',
    tone: 'safe',
    icon: 'Shield',
    description: 'Moves every task off Google models, onto other models QuoteMaker has tested.',
    evidence: 'Apply only when Google models stop working on the account. It is not a default: Back to defaults returns to QuoteMaker\'s tested models.',
    replacesAll: true,
    embeddingModel: RJ_BIRD_EMBEDDING_MODEL,
    overrides: Object.freeze(Object.fromEntries(
        Object.entries(RJ_BIRD_PRIVACY_SAFE_OVERRIDES).filter(([taskName]) => !groupForTask(taskName)?.inactive),
    )),
});

// Backward-compat alias - old code referenced TASK_MODEL_MAP
export const TASK_MODEL_MAP = TASK_MODEL_RECOMMENDATIONS;

/** Normalise a TASK_MODEL_RECOMMENDATIONS entry to its model id string. */
export function readModelFromRecommendation(entry) {
    if (!entry) return '';
    if (typeof entry === 'string') return entry;
    if (typeof entry === 'object' && typeof entry.model === 'string') return entry.model;
    return '';
}

/** Look up the recommendation entry (string or object) for a task. */
export function getRecommendationEntry(taskName) {
    return TASK_MODEL_RECOMMENDATIONS[taskName];
}

const AVAILABILITY_TASK_ALIASES = Object.freeze({
    // The Stage 1 bulk classifier's prompt/telemetry id is batch-classify, but
    // its canonical model Lane is doc-classify.
    'batch-classify': DOCUMENT_CLASSIFICATION_MODEL_TASK,
});

/** True when OpenRouter has no usable provider endpoint for this model id. */
export function isUnavailableOpenRouterModel(modelId) {
    return String(modelId || '').trim() === UNAVAILABLE_GEMINI_FLASH_LITE;
}

/**
 * Resolve only known-unavailable model ids to a bounded task-aware Lane.
 *
 * This is deliberately narrower than the general provider failover code: it
 * does not catch request errors, change tenant policy, alter authentication or
 * bypass spend controls. It prevents a persisted global/per-task setting from
 * repeatedly sending a model slug already proven to return a hard 404. The
 * caller receives both requested and effective ids so the UI/Pipeline Monitor
 * can show the recovery instead of presenting it as the operator's selection.
 */
export function resolveUnavailableModelLane(taskName, modelId) {
    const requestedModel = String(modelId || '').trim();
    if (!isUnavailableOpenRouterModel(requestedModel)) {
        return {
            requestedModel,
            effectiveModel: requestedModel,
            availabilityRerouted: false,
            availabilityWarning: '',
        };
    }

    const canonicalTask = AVAILABILITY_TASK_ALIASES[taskName] || taskName;
    // Preserve the operator's Flash-Lite intent (price, latency and modality)
    // by normalising only the stale preview suffix to the live stable sibling.
    // Protected task recommendations still win earlier in the normal Lanes
    // precedence when no explicit override requested the preview model.
    const effectiveModel = GEMINI_LITE;

    return {
        requestedModel,
        effectiveModel,
        availabilityRerouted: true,
        availabilityWarning: `Requested model "${requestedModel}" has no usable OpenRouter endpoint; the ${canonicalTask || 'unregistered'} Lane will use "${effectiveModel}" for this call.`,
    };
}

// ────────────────────────────────────────────────────────────────────────────
// Assistant model fallback chain (added 2026-06-29, Peter).
// When the QM Assistant's active model hits a TRANSIENT provider outage (5xx
// "temporarily unavailable", rate limit, key cooldown, network blip), the agent
// silently retries on an alternative model instead of surfacing a scary banner
// (see assistant-agent.js model-fallback loop + isRetryableAssistantError).
// Sourced here (not hardcoded in the agent) so model IDs stay in the registry
// per the routing HARD RULE.
// ────────────────────────────────────────────────────────────────────────────
// Rebuilt 2026-09-29 from MEASURED models only, in measured rank order (pass
// rate, then reliability, then latency, then cost; the table sits above
// ASSISTANT_MODEL). Each vendor appears once, so the outage that stopped the
// primary cannot also stop its fallback: DeepSeek is served by many hosts
// through OpenRouter, Qwen by Alibaba, Grok by xAI. A model flagged unusable in
// the Assistant (ASSISTANT_UNUSABLE_MODELS) is never added. gpt-5.6-terra, the
// old floor, measured 116 of 132 and Azure's content filter refused every
// prompt-injection case, so it is no longer in the chain.
export const ASSISTANT_MEASURED_MODELS = Object.freeze([ASSISTANT_MODEL, QWEN_MAX, GROK_47]);
const ASSISTANT_MAX_FALLBACKS = 2;
// First fallback for the default primary. Kept for callers that name one floor.
export const ASSISTANT_FALLBACK_MODEL = QWEN_MAX;

function _modelVendor(modelId) {
    return String(modelId || '').trim().toLowerCase().split('/')[0];
}

/**
 * Ordered, de-duplicated model chain the assistant tries on a transient
 * provider failure: the active model first (whatever the operator chose, even a
 * flagged one), then up to two measured fallbacks from vendors not already in
 * the chain. The agent advances to the next entry only when the current one
 * fails with a retryable provider error.
 * @param {string} primaryModel - resolved active model (pinned or recommended)
 * @returns {string[]}
 */
export function getAssistantFallbackModels(primaryModel) {
    const chain = [];
    const primary = (typeof primaryModel === 'string' && primaryModel.trim()) ? primaryModel.trim() : '';
    if (primary) chain.push(primary);
    const vendors = new Set(chain.map(_modelVendor));
    let added = 0;
    for (const candidate of ASSISTANT_MEASURED_MODELS) {
        if (added >= ASSISTANT_MAX_FALLBACKS) break;
        if (chain.includes(candidate)) continue;
        if (vendors.has(_modelVendor(candidate))) continue;
        if (isAssistantUnusableModel(candidate)) continue;
        chain.push(candidate);
        vendors.add(_modelVendor(candidate));
        added += 1;
    }
    return chain;
}

/**
 * Return the list of task names flagged strongRecommendation = true.
 * These are Peter-tuned picks that should not be casually overridden.
 */
export function getStrongRecommendations() {
    return Object.entries(TASK_MODEL_RECOMMENDATIONS)
        .filter(([, entry]) => entry && typeof entry === 'object' && entry.strongRecommendation === true)
        .map(([name]) => name);
}

/** True if the task has strongRecommendation = true. */
export function isStrongRecommendation(taskName) {
    const entry = TASK_MODEL_RECOMMENDATIONS[taskName];
    return !!(entry && typeof entry === 'object' && entry.strongRecommendation === true);
}

/** Return the reason string for a strong-recommendation task, or ''. */
export function getStrongRecommendationReason(taskName) {
    const entry = TASK_MODEL_RECOMMENDATIONS[taskName];
    return (entry && typeof entry === 'object' && typeof entry.reason === 'string') ? entry.reason : '';
}

// ────────────────────────────────────────────────────────────────────────────
// AI-6 audit 2026-04-21: mis-classified global detection
// ----------------------------------------------------------------------------
// Peter's common global choice is `gemini-3.1-flash-lite-preview`, which is a
// Tier 1 classifier. Routing reasoning-heavy tasks through it silently drops
// quote quality. This helper returns the list of tasks where the current
// global is below the strong-recommendation tier — the UI specialist wires
// it to a banner on the AI Models + Settings pages.
//
// The rule is simple: if a task has strongRecommendation=true AND the global
// is NOT in the same model family as the strong recommendation (Sonnet ≥,
// Opus ≥, or Pro ≥), flag it. Users can silence per-task with an explicit
// override (that's the whole point of the override mechanism).
// ────────────────────────────────────────────────────────────────────────────

/** Coarse tier rank for comparing models. Higher number = more capable. */
function _coarseTierRank(modelId) {
    if (!modelId || typeof modelId !== 'string') return 0;
    const id = modelId.toLowerCase();
    // Tier 5 (Opus / GPT-5-pro / Gemini Pro)
    if (id.includes('opus')) return 5;
    // 2026-09-29: the GPT-6 and Fable flagships sit with Opus; GPT-6 Sol and
    // Luna sit with the GPT-5 line below. Without these every GPT-6 id fell to
    // the optimistic Tier 2 default.
    if (id.includes('gpt-6-astra') || id.includes('claude-fable')) return 5;
    // `gpt-6.1-sol` (2026-10-08) carries a minor version the dash-only
    // pattern missed, which dropped it to the optimistic Tier 2 default.
    if (id.match(/gpt-6(?:\.\d+)?-/)) return 4;
    if (id.includes('gpt-5') && id.includes('pro')) return 5;
    if (id.includes('gemini') && id.includes('pro')) return 5;
    // Tier 4 (Sonnet / GPT-5 / GPT-4o)
    if (id.includes('sonnet')) return 4;
    if (id.match(/gpt-5(?!-pro)/)) return 4;
    if (id.includes('gpt-4o')) return 4;
    // Tier 3 (Haiku 4.x)
    if (id.includes('haiku') && !id.includes('haiku-3')) return 3;
    // Tier 2 (Flash - NOT flash-lite)
    if (id.includes('flash') && !id.includes('flash-lite')) return 2;
    // Tier 1 (flash-lite / mini / haiku-3)
    if (id.includes('flash-lite')) return 1;
    if (id.includes('mini')) return 1;
    if (id.includes('haiku-3')) return 1;
    return 2; // unknown → optimistically call it Tier 2 so we don't over-warn
}

/**
 * Return an array of `{ taskName, taskLabel, required, recommended }` entries
 * where the user's resolved global model is below the strong-recommendation
 * tier. An explicit per-task override silences the warning for that task.
 *
 * @param {string} globalModel - The user's current global (from Settings / qm_model)
 * @returns {Array<{taskName:string,taskLabel:string,globalTier:number,recommendedTier:number,recommendedModel:string,reason:string}>}
 */
export function detectGlobalBelowRecommendedTier(globalModel) {
    if (!globalModel || typeof globalModel !== 'string') return [];
    const globalTier = _coarseTierRank(globalModel);

    // Per-task overrides silence the warning per task.
    let overrides = {};
    if (typeof window !== 'undefined') {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            overrides = raw ? JSON.parse(raw) : {};
        } catch { /* noop */ }
    }

    const flagged = [];
    for (const [taskName, entry] of Object.entries(TASK_MODEL_RECOMMENDATIONS)) {
        if (!entry || typeof entry !== 'object' || entry.strongRecommendation !== true) continue;
        if (overrides[taskName] && String(overrides[taskName]).trim()) continue; // user override silences
        const recommended = entry.model || '';
        const recommendedTier = _coarseTierRank(recommended);
        if (globalTier >= recommendedTier) continue; // global meets or beats
        flagged.push({
            taskName,
            taskLabel: TASK_METADATA[taskName]?.label || taskName,
            globalTier,
            recommendedTier,
            recommendedModel: recommended,
            reason: typeof entry.reason === 'string' ? entry.reason : '',
        });
    }
    return flagged;
}

// ────────────────────────────────────────────────────────────────────────────
// MEDIA TASKS - vision, video, and audio tasks that must use a media-capable
// model. These are pinned to the tier recommendation over the user's global
// model (see pickModelForTask priority 2.6) because media capability differs
// sharply across providers: routing a PDF-OCR or photo-description call to a
// text-only or inconsistent-vision model produces a 400 from OpenRouter and
// breaks the import pipeline entirely. Users can still override per-task via
// the AI Models page (localStorage, priority 2).
// ────────────────────────────────────────────────────────────────────────────
export const MEDIA_TASKS = new Set([
    'image-ocr',
    'image-text-triage',
    'pdf-ocr',
    'plan-analysis',
    'photo-label',
    'photo-description',
    'photo-analysis',
    'photo-forensic',
    'photo-forensic-fallback',
    'photo-auto-assign',
    'photo-auto-assign-fallback',
    'audio-transcription',
    'video-processing',
    'key-moments',
]);

// Vision tasks pass image/PDF/plan/photo content to the model. Key-moment
// extraction works from a transcript, so it stays out of this set even though
// it lives in the media stage grouping.
export const VISION_TASKS = new Set([
    'image-ocr',
    'image-text-triage',
    'pdf-ocr',
    'plan-analysis',
    'photo-label',
    'photo-description',
    'photo-analysis',
    'photo-forensic',
    'photo-forensic-fallback',
    'photo-auto-assign',
    'photo-auto-assign-fallback',
    // Job import: a PDF quote reaches the model as page images.
    'quote-import-extraction',
]);

// ────────────────────────────────────────────────────────────────────────────
// AUDIO/VIDEO TASKS - narrower subset of MEDIA_TASKS. These pass an audio
// data URL through OpenRouter's `image_url` channel, which Gemini + GPT-4o
// handle but Anthropic Claude (Sonnet/Opus/Haiku) rejects with 400. When
// the resolved model for one of these tasks is NOT in the audio-capable set
// we redirect to an audio-safe fallback rather than let the call blow up.
// The user's override still wins (they may know what they're doing), but
// only if their override is itself audio-capable - otherwise we fail closed
// to a working model and log a warning.
// ────────────────────────────────────────────────────────────────────────────
export const AUDIO_VIDEO_TASKS = new Set([
    'audio-transcription',
    'video-processing',
]);

/**
 * Is this model capable of taking audio/video input via OpenRouter's
 * `image_url` channel? Heuristic allowlist - conservative by design. Any
 * provider we haven't verified returns false so the picker falls back.
 *
 * Verified audio-capable families (April 2026):
 *   google/gemini-*            - all Gemini 2.5+ variants accept audio base64
 *   openai/gpt-audio*          - native multimodal audio models
 *   openai/whisper-*           - native transcription models
 *   mistralai/voxtral-*        - native audio understanding/transcription
 *
 * Known incompatible:
 *   anthropic/claude-*         - vision yes, audio NO (returns 400)
 */
export function isAudioCapableModel(modelId) {
    if (!modelId || typeof modelId !== 'string') return false;
    const id = modelId.toLowerCase();
    if (id.startsWith('anthropic/')) return false;               // Claude: no audio
    if (id.startsWith('google/gemini-')) return true;            // Gemini: yes
    if (id.startsWith('openai/gpt-audio')) return true;          // GPT Audio / Mini
    if (id.startsWith('openai/whisper')) return true;            // Whisper: yes
    // Voxtral speech synthesis accepts text and emits audio; it cannot ingest
    // the audio/video payload used by transcription routes.
    if (id.startsWith('mistralai/voxtral-mini-tts')) return false;
    if (id.startsWith('mistralai/voxtral-')) return true;        // Voxtral understanding/transcription
    // Default closed - unknown providers redirect to the fallback.
    return false;
}

/**
 * Is this model capable of accepting image/PDF/plan/photo inputs through the
 * OpenRouter multimodal message shape?
 */
export function isVisionCapableModel(modelId) {
    if (!modelId || typeof modelId !== 'string') return false;
    const id = modelId.toLowerCase();
    if (id.startsWith('google/gemini-')) return true;
    if (id.startsWith('openai/gpt-4o')) return true;
    if (id.startsWith('openai/gpt-5')) return true;
    if (id.startsWith('anthropic/claude-')) return true;
    if (id.startsWith('moonshotai/kimi-k2.6') || id.startsWith('moonshot/kimi-k2.6') || id.includes('kimi-k2-6')) return true;
    // 2026-10-26 catalogue: Kimi K3.1 Pro takes text, image and video.
    if (id.startsWith('moonshotai/kimi-k3.1-pro')) return true;
    if (id.startsWith('qwen/qwen3.5-plus')) return true;
    if (id.startsWith('z-ai/glm-5v') || id.includes('glm-5v')) return true; // GLM vision (e.g. glm-5v-turbo)
    // 2026-09-29 catalogue: each of these lists image input. DeepSeek, Qwen and
    // Grok 4.7 were also read-tested on a real JPEG that day. glm-5.3 (no
    // suffix) stays text-only; only the Flash build takes images.
    // GPT-6.1 (2026-10-08) is `openai/gpt-6.1-...`, which the dash prefix
    // missed, so a GPT-6.1 pick on a vision lane was rerouted away.
    if (/^openai\/gpt-6(?:\.\d+)?-/.test(id)) return true;
    if (id.startsWith('x-ai/grok-4.6') || id.startsWith('x-ai/grok-4.7')) return true;
    if (id.startsWith('qwen/qwen3.8-max')) return true;
    if (id.startsWith('z-ai/glm-5.3-flash')) return true;
    if (id.startsWith('deepseek/deepseek-v4.1-flash')) return true;
    return false;
}

// Fallback model for vision tasks that have no specific tier recommendation.
// The stable Flash-Lite sibling was verified live on 2026-07-20.
const MEDIA_FALLBACK_MODEL = GEMINI_LITE;
// Audio/video-safe fallback. Stage 1 extracts a video's audio before this
// route, so native Voxtral audio understanding is the correct fail-closed path.
// Keeping the fallback non-Google also prevents an incompatible stale override
// from silently recreating RJ Bird's OpenRouter data-policy error.
const AUDIO_VIDEO_FALLBACK_MODEL = VOXTRAL_SMALL;

/**
 * Human-readable metadata for the AI Models configuration UI.
 * Each entry: { label, description, defaultsTo, costTier ('cheap'|'medium'|'premium') }
 */
export const TASK_METADATA = {
    'image-ocr':            { label: 'Image OCR',                description: 'Extract text from photos of documents (Stage 1)',                                            costTier: 'cheap' },
    'image-text-triage':    { label: 'Image Text Check',         description: 'Sort imported images into documents, site photos with writing, and plain photos before OCR (Stage 1)', costTier: 'cheap' },
    'pdf-ocr':              { label: 'PDF OCR (image-based)',    description: 'Extract text from scanned/image-based PDFs via vision (Stage 1)',                            costTier: 'cheap' },
    'photo-label':          { label: 'Photo Labeling',           description: 'Auto-label site photos with content categories (Stage 1)',                                   costTier: 'cheap' },
    'photo-description':    { label: 'Photo Description',        description: 'Per-photo contextual captions generated during Stage 1 import. Provides vision-grounded text the rest of the pipeline can cite.',  costTier: 'medium' },
    'plan-analysis':        { label: 'Plan Analysis',            description: 'Reads architectural / engineering / DA plan drawings during Stage 1 to surface layout, room schedules, and annotations the text-only prompts would otherwise miss.', costTier: 'medium' },
    'photo-forensic':       { label: 'Photo Forensic',           description: 'Per-photo failure-mechanism reasoning with inspector-note cross-reference (Stage 1.75)',   costTier: 'medium' },
    'photo-forensic-fallback': { label: 'Photo Forensic — Fallback', description: 'Independent Sonnet vision route used once for a batch when the protected primary fails or returns invalid JSON (Stage 1.75)', costTier: 'premium' },
    'evidence-canonicalisation': { label: 'Evidence Canonicaliser', description: 'Normalise notes/RFQ/photos/transcripts into citable canonical_evidence (Stage 1.75)',  costTier: 'medium' },
    'rfq-mode-classify':    { label: 'RFQ Mode Classifier',      description: 'Classifies RFQ as prescriptive / investigative / hybrid (Stage 1.75)',                     costTier: 'cheap' },
    'locality-inference':   { label: 'Locality Inference (fallback)', description: 'Last-resort AI locality guess when deterministic resolver fails (Stage 1.75)',        costTier: 'cheap' },
    'diagnosis-pass-a':     { label: 'Diagnosis Pass A - Builder\u2019s Eye',     description: 'Practical mechanical-failure persona on the ordinary Gemini lane (Stage 1.75)', costTier: 'standard' },
    'diagnosis-pass-b':     { label: 'Diagnosis Pass B - Engineer\u2019s Eye',    description: 'Structural and long-term degradation persona on the ordinary Gemini lane (Stage 1.75)', costTier: 'standard' },
    'diagnosis-pass-c':     { label: 'Diagnosis Pass C - Devil\u2019s Advocate',  description: 'Evidence challenge persona on the ordinary Gemini lane (Stage 1.75)', costTier: 'standard' },
    'diagnosis-reconciliation': { label: 'Diagnosis Reconciliation',              description: 'Fuses the diagnosis personas into one authoritative ProjectDiagnosis (Stage 1.75)', costTier: 'standard' },
    'doc-classify':         { label: 'Document Classification',  description: 'Classify uploaded files as RFQ / notes / report (Stage 1). Also the model route for bulk/batch document classification.', costTier: 'cheap' },
    'section-extract':      { label: 'Section Extraction',      description: 'Extract important sections from long technical documents (for the project summary)',                             costTier: 'medium' },
    'rationale-precompute': { label: 'Rationale Pre-compute',  description: 'Pre-generate quote rationale for scope items (Stage 3)',                                        costTier: 'medium' },
    'rfq-summary':          { label: 'RFQ Summary',              description: 'Summarise RFQ content for the prompt (Stage 1)',                                             costTier: 'cheap' },
    'rfq-analysis':         { label: 'RFQ Analysis',             description: 'Deep RFQ scope analysis (Stage 1)',                                                          costTier: 'medium' },
    'key-moments':          { label: 'Video Key Moments',        description: 'Identify timestamps in transcripts for frame extraction (Stage 1)',                          costTier: 'cheap' },
    'notes-scoring':        { label: 'Notes Quality Scoring',    description: 'AI scoring of inspector-notes quality (Stage 1); the live route.',                            costTier: 'cheap' },
    'semantic-tag':         { label: 'Semantic Tagging',         description: 'Generate tags for gold standards (retrieval / Stage 1)',                                     costTier: 'cheap' },
    'audio-transcription':  { label: 'Audio Transcription',      description: 'Transcribe voice memos and video audio (Stage 1)',                                           costTier: 'medium' },
    'intake-email-extract': { label: 'Intake — Email Extraction', description: 'Parse a pasted email body or dropped .eml into structured RFQ fields (requester, property, scope, QR, SP, urgency). Cheap structured-output task.', costTier: 'cheap' },
    'site-visit-report-tidy': { label: 'Site Visits: Report tidy', description: 'Tidy a dictated site assessor visit report into sections (access, findings, measurements, recommended work, questions) without adding or dropping facts.', costTier: 'medium' },
    'intake-sdp-qc-assessment': { label: 'Intake — SDP QC Assessment', description: 'Cross-reference the Site Documentation Package against the original RFQ + canonical category profile when an intake job enters sdp-pending-qc. Emits a structured verdict (sufficient / partial / insufficient) + recommended next step (proceed / phone / email / site-revisit). Sonnet tier; vision-capable for photo assessment.', costTier: 'medium' },
    'video-processing':     { label: 'Video Processing',         description: 'Process video files for analysis (Stage 1)',                                                 costTier: 'medium' },
    'photo-auto-assign':    { label: 'Photo Auto-Assign',        description: 'Suggest which scope items each photo supports (Stage 3)',                                    costTier: 'medium' },
    'photo-auto-assign-fallback': { label: 'Photo Auto-Assign — Fallback', description: 'Independent Sonnet vision route used once when the primary photo assignment call fails or returns invalid structured output (Stage 3).', costTier: 'premium' },
    // 2026-05-02 (OW-102): 'kpi-scoring' label removed. See comment above.
    'cost-recompute':       { label: 'Cost Recompute',           description: 'Auto-fired by QM Assistant when scope item inclusions change \u2014 regenerates the per-item cost breakdown (Stage 3)', costTier: 'medium' },
    'market-price-research': { label: 'Market Price Research',   description: 'Searches current Australian supplier listings only for material/equipment gaps absent from the Francis rate card; citations and the 30% uplift are validated deterministically (Stage 3).', costTier: 'medium' },
    'assessor-extraction':  { label: 'Inspector Extraction',      description: 'Extract inspector name + visit date from notes (Stage 1)',                                    costTier: 'cheap' },
    'project-summary':      { label: 'Project Summary',          description: 'Best-guess interpretation of RFQ + photos + notes before Risk Analysis (the summary in the Job cockpit)',          costTier: 'cheap' },
    'summary-tts':          { label: 'Summary Voice (TTS)',      description: 'Reads the summary in the Job cockpit aloud. Voxtral Mini TTS through the backend speech gateway',                              costTier: 'cheap' },
    'contact-extraction':   { label: 'Contact Extraction',       description: 'CRITICAL - extract client name, address, strata plan, order number from RFQ (Stage 1)',     costTier: 'premium' },
    'classification-verifier': { label: 'Classification Verifier', description: 'Post-deterministic sanity-check on the extracted client. Reviews all docs to catch obvious misclassifications (e.g. inspector sender miscast as client). Kill switch: qm_disable_classification_verifier.', costTier: 'cheap' },
    'inspector-feedback':   { label: 'Inspector Feedback',         description: 'Reviews Stage 1 data-collection coverage and returns structured coaching for the inspector (Stage 2).', costTier: 'cheap' },
    'report-synthesis':     { label: 'Report Synthesis (Stage 2)',  description: 'Generates executive summary, pricing rationale, and methodology from completed scope analysis - Phase 2 of the split pipeline',  costTier: 'cheap' },
    'risk-analysis':        { label: 'Risk Analysis (Stage 2)',  description: 'Generate the full scope + risk analysis from project data',                                  costTier: 'premium' },
    'quote-generation':     { label: 'Quote Generation (Stage 4)', description: 'Generate the final quote document',                                                        costTier: 'premium' },
    'ai-assistant':         { label: 'AI Assistant — Chat (normal route)', description: 'The everyday QM Assistant conversation + tool loop (all stages). Defaults to DeepSeek V4.1 Flash, the best model measured on the real Assistant request on 2026-09-29, at the reasoning effort it was measured at.', costTier: 'premium' },
    'ai-assistant-deep':    { label: 'AI Assistant — Think Harder (deep route)', description: 'The same QM Assistant on an Opus-class model. Used only when your message asks for it in plain words: "think harder", "think deeper", "think hard about" or "use the deep model". A reply written this way shows "Thought harder". Slower and dearer, deeper synthesis.', costTier: 'premium' },
    'ai-assistant-light':   { label: 'AI Assistant — Light', description: 'Fast QM Assistant route for quick edits and lookups. Uses DeepSeek V4.1 Flash, the same measured route as Chat, without forcing an extended-reasoning tier.', costTier: 'cheap' },
    'assistant-compaction': { label: 'Assistant Conversation Compaction', description: 'Summarises older QM Assistant turns once the conversation token count exceeds the compaction threshold. Uses GPT-5.4 Nano, the model measured for it on 2026-09-29.', costTier: 'cheap' },
    'model-advisor':        { label: 'Model Advisor (Review with AI)', description: 'The in-app "Review with AI" tool on this page: an AI reasons over each function\'s current model + the live OpenRouter catalogue and proposes keep-or-switch. Opus-class meta-reasoner. Routes nothing in the quote pipeline.', costTier: 'premium' },
    'work-method-statement':{ label: 'Retired per-item Work Method route', description: 'Compatibility-only route. The ordinary workflow uses one project-level Stage 3 Work Method inference.', costTier: 'premium' },
    'learning-triage':      { label: 'Learning Triage (M2 Phase B)', description: 'Reads candidate learnings retrieved by cosine similarity, decides which genuinely apply, flags contradictions, and produces per-learning rationale for the main prompt.', costTier: 'premium' },
    'learning-harvest':     { label: 'Learning Harvest',         description: 'After save/won/lost, extracts candidate learnings from the before/after diff with rationale. Routes to Proposed Learnings queue or auto-commits if confidence is very high.', costTier: 'medium' },
    'project-strategy':     { label: 'Project Strategy',        description: 'Method-first Phase 1. Given the diagnosis and bios, generates 3-5 alternative approaches (full replacement / targeted repair / overlay / staged investigation / monitor / consolidated), scores fit to site + client, picks one, and stores rationales for the rejected options.', costTier: 'premium' },
    'project-strategy-fallback': { label: 'Project Strategy — Fallback', description: 'Second visible Lanes route used only when the primary Project Strategy model is blocked by OpenRouter data policy or a transient provider outage. A failed fallback leaves Strategy incomplete instead of reusing stale content.', costTier: 'premium' },
    'project-strategy-recovery': { label: 'Project Strategy — Independent Recovery', description: 'Third visible Lanes route. Uses an independent non-Anthropic model after the primary and first fallback are unavailable.', costTier: 'premium' },
    'project-strategy-auto': { label: 'Project Strategy — Governed Recovery', description: 'Final visible Lanes route. Uses the named GPT-5.6 Terra recovery model after Gemini 3.7 Flash is unavailable; OpenRouter auto-selection is not permitted.', costTier: 'premium' },
    'wms-generation':       { label: 'Work Method (project document)', description: 'Method-first Phase 12. Produces the builder\u2019s execution plan \u2014 the project-level Work Method doubling as the contractor brief. Consolidates per-item workMethod, materials (from cost_breakdown), equipment, trades, sequence, and standards into a single JSON document rendered to PDF for field distribution. Not a SWMS (regulatory WHS document) \u2014 this is methodology + execution, not hazards + controls.', costTier: 'premium' },
    // v5.41.104 (2026-06-17): previously-unregistered tasks, now manageable.
    'inclusions-generator':     { label: 'Inclusions Generator',      description: 'Generates the inclusions list for a single scope item when the Assistant or operator asks (Stage 4).', costTier: 'cheap' },
    'title-suggester':          { label: 'Scope Title Suggester',     description: 'Suggests a concise client-facing title for a scope item (Stage 4).', costTier: 'cheap' },
    'quote-change-narrative':   { label: 'Quote Change Narrative',    description: 'Writes the plain-English "what changed and why" narrative for a re-generated quote (Stage 4/5).', costTier: 'cheap' },
    'requote-scope-extraction': { label: 'Requote Scope Extraction',  description: 'Extracts the prior scope from an uploaded existing/competitor quote so it can be re-priced (Stage 1). Extraction-tier — defaults to Haiku.', costTier: 'cheap' },
    'rfq-fit-verify':           { label: 'Independent Quote Issue Critic', description: 'Cross-provider review of RFQ coverage, diagnosis/remediation, cause-first sequencing, Work Method coherence and client-safe copy. It never changes prices; major findings block issue.', costTier: 'premium' },
    'scope-morph':              { label: 'Client Scope Morph',        description: 'Transforms the approved work method into concise client-facing scope wording.', costTier: 'premium' },
    'pdf-comparison':           { label: 'Final PDF Comparison',      description: 'Compares the generated quote with the final sent PDF and proposes structured learnings.', costTier: 'cheap' },
    'gold-standard-extraction': { label: 'Gold Standard Extraction',  description: 'Extracts structured scope, pricing, and voice evidence from an approved quote PDF.', costTier: 'cheap' },
    'quote-import-extraction':  { label: 'Quote Import Extraction',   description: 'Reads an existing quote document (PDF, DOCX, DOC or RTF, from any builder) into items, amounts, GST basis and stated totals so it can be imported as a Job at Stage 5.', costTier: 'medium' },
    'quote-paste-structure':    { label: 'Paste Quote Text (find items)', description: 'Stage 5 Paste quote text: when code cannot split a pasted quote into items, labels each pasted line (title, description, line, subheading, exclusion, note, unused). It writes no words; the quote keeps the pasted text.', costTier: 'cheap' },
    'email-generation':         { label: 'Quote Delivery Email',       description: 'Writes the short subject and body used when delivering the final quote to the client.', costTier: 'cheap' },
    'work-order-draft':         { label: 'Work Order Draft',           description: 'Creates the AI-enriched phase and trade brief queued after a quote is marked won.', costTier: 'premium' },
    'paired-transformation-analysis': { label: 'Paired Training Transformation', description: 'Learns how the builder transformed raw RFQ evidence into the approved final quote.', costTier: 'premium' },
    'category-profile-redistillation': { label: 'Category Profile Re-distillation', description: 'Rebuilds canonical job-category patterns from the approved training corpus.', costTier: 'premium' },
    'batch-trainer-extract':    { label: 'Batch Trainer Extraction',  description: 'Extracts one training corpus into the QuoteMaker training schema.', costTier: 'cheap' },
    'batch-trainer-tag-facets': { label: 'Batch Trainer Facets',      description: 'Tags extracted training scope and voice samples with typed learning facets.', costTier: 'cheap' },
    'batch-trainer-mine-insights': { label: 'Batch Trainer Insights', description: 'Aggregates cross-corpus training insights when AI arbitration is enabled.', costTier: 'premium' },
};

// ── Assistant pin migration (2026-09-29) ────────────────────────────────────
// A per-task override in localStorage qm_task_models outranks every protected
// route, so rerouting the Assistant in the registry does nothing for a browser
// that pinned a flagged model onto a task it is flagged on (an Assistant chat
// lane or compaction; through the header model switcher, the AI Models page, a
// gear or the Assistant's own override tool, whose set_all_tasks pins every
// task). This migration removes such a pin ONCE, so the task returns to its
// protected route, and records a one-line notice for the AI Models page. The
// marker makes it a migration and not a ban: a pick made after it is the
// operator's deliberate choice and is kept (Q1). Pins of the same model on
// tasks it is not flagged on are never touched. Tenant AI policies hold only an allowlist and
// no model pin, so there is nothing to migrate on the server.
// A saved gear (qm_model_gear_sets_v1) is a stored snapshot of qm_task_models,
// and applying one replaces the live overrides, so the same pass takes the
// same flagged pins out of every saved gear. Otherwise a gear saved before the
// reroute would put the model back the first time it is applied. A gear saved
// after the migration is, like a live pick, the operator's choice and is kept.
export const ASSISTANT_PIN_MIGRATION_KEY = 'qm_assistant_unusable_pin_migration_v1';
export const ASSISTANT_ROUTE_NOTICE_KEY = 'qm_assistant_route_notice_v1';

const ASSISTANT_LANE_LABELS = Object.freeze({
    'ai-assistant': 'Chat',
    'ai-assistant-light': 'Light',
    'ai-assistant-deep': 'Think Harder',
    'assistant-compaction': 'Compaction',
});

function _defaultStorage() {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
}

function _parseStoredOverrides(raw) {
    try {
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

/**
 * The saved gears with every flagged pin taken out, and one entry per pin
 * taken out ({ gear, task, from, to }). Gears keep every other field as
 * stored. Returns null gears when the store is missing or unreadable.
 */
function _migrateSavedGearPins(storage) {
    let parsed;
    try {
        parsed = JSON.parse(storage.getItem(CUSTOM_GEAR_STORAGE_KEY) || 'null');
    } catch {
        return { gears: null, moved: [] };
    }
    if (!Array.isArray(parsed)) return { gears: null, moved: [] };
    const moved = [];
    const gears = parsed.map((gear, index) => {
        const stored = gear && typeof gear === 'object' ? gear.overrides : null;
        if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return gear;
        let nextOverrides = null;
        for (const task of ASSISTANT_FLAGGED_TASKS) {
            const pinned = typeof stored[task] === 'string' ? stored[task].trim() : '';
            if (!pinned || !_taskFlag(pinned, task)) continue;
            nextOverrides = nextOverrides || { ...stored };
            delete nextOverrides[task];
            moved.push({
                gear: _normalizeCustomGearPreset(gear, index)?.name || `Custom Gear ${index + 1}`,
                task,
                from: pinned,
                to: readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[task]) || '',
            });
        }
        return nextOverrides ? { ...gear, overrides: nextOverrides } : gear;
    });
    return { gears, moved };
}

/**
 * Move stored pins of a flagged model off each task it is flagged on (the
 * Assistant chat lanes and compaction), in the live overrides and in every
 * saved gear, once. Returns the overrides the caller should use (the flagged
 * pins removed), the live moves made and the gear pins taken out. When writes
 * are not allowed (persistMigration false, or an eval single-attempt run)
 * nothing is written, but the returned overrides still keep the Assistant off
 * the pin.
 */
export function migrateAssistantUnusablePins(storage = _defaultStorage(), { persistMigration = true } = {}) {
    if (!storage) return { overrides: {}, moved: [], gearMoves: [] };
    let overrides;
    try {
        overrides = _parseStoredOverrides(storage.getItem(STORAGE_KEY));
    } catch {
        return { overrides: {}, moved: [], gearMoves: [] };
    }
    let alreadyMigrated = false;
    try {
        alreadyMigrated = storage.getItem(ASSISTANT_PIN_MIGRATION_KEY) === '1';
    } catch { /* unreadable marker: migrate in memory below */ }
    if (alreadyMigrated) return { overrides, moved: [], gearMoves: [] };

    const next = { ...overrides };
    const moved = [];
    for (const task of ASSISTANT_FLAGGED_TASKS) {
        const pinned = typeof next[task] === 'string' ? next[task].trim() : '';
        if (!pinned || !_taskFlag(pinned, task)) continue;
        delete next[task];
        moved.push({ task, from: pinned, to: readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[task]) || '' });
    }

    const writesAllowed = persistMigration !== false && !isEvalAiSingleAttemptPolicyActive();
    const savedGears = writesAllowed ? _migrateSavedGearPins(storage) : { gears: null, moved: [] };
    const gearMoves = savedGears.moved;
    if (writesAllowed) {
        try {
            if (moved.length) {
                if (Object.keys(next).length) {
                    storage.setItem(STORAGE_KEY, JSON.stringify(next));
                } else if (typeof storage.removeItem === 'function') {
                    storage.removeItem(STORAGE_KEY);
                }
            }
            if (gearMoves.length) storage.setItem(CUSTOM_GEAR_STORAGE_KEY, JSON.stringify(savedGears.gears));
            if (moved.length || gearMoves.length) {
                storage.setItem(ASSISTANT_ROUTE_NOTICE_KEY, JSON.stringify({ moved, gears: gearMoves, at: new Date().toISOString() }));
            }
            storage.setItem(ASSISTANT_PIN_MIGRATION_KEY, '1');
        } catch { /* blocked or full storage: the in-memory result still applies */ }
        if (moved.length) {
            _emitTaskModelOverridesChanged(_sanitizeTaskModelOverrides(next), {
                source: 'assistant-route-migration',
                mode: 'assistant_unusable_pins',
            });
        }
    }
    return { overrides: next, moved, gearMoves };
}

/** The recorded migration notice, or null when nothing was moved or it was dismissed. */
export function getAssistantRouteNotice(storage = _defaultStorage()) {
    if (!storage) return null;
    try {
        const parsed = JSON.parse(storage.getItem(ASSISTANT_ROUTE_NOTICE_KEY) || 'null');
        const isMove = m => m && typeof m.task === 'string' && typeof m.from === 'string';
        const moved = Array.isArray(parsed?.moved)
            ? parsed.moved.filter(isMove)
                .map(m => ({ task: m.task, from: m.from, to: typeof m.to === 'string' ? m.to : '' }))
            : [];
        const gears = Array.isArray(parsed?.gears)
            ? parsed.gears.filter(m => isMove(m) && typeof m.gear === 'string')
                .map(m => ({ gear: m.gear, task: m.task, from: m.from, to: typeof m.to === 'string' ? m.to : '' }))
            : [];
        return moved.length || gears.length
            ? { moved, gears, at: typeof parsed.at === 'string' ? parsed.at : '' }
            : null;
    } catch {
        return null;
    }
}

export function dismissAssistantRouteNotice(storage = _defaultStorage()) {
    try {
        if (storage && typeof storage.removeItem === 'function') storage.removeItem(ASSISTANT_ROUTE_NOTICE_KEY);
    } catch { /* best-effort */ }
}

function _joinWords(words) {
    if (words.length <= 1) return words.join('');
    return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/**
 * One line for the AI Models page: what moved, where to, and why. Pins taken
 * out of saved gears are named in the same line, with the gears' names.
 */
export function formatAssistantRouteNotice(notice) {
    const moved = Array.isArray(notice?.moved) ? notice.moved : [];
    const gearMoves = Array.isArray(notice?.gears) ? notice.gears : [];
    if (!moved.length && !gearMoves.length) return '';
    const all = [...moved, ...gearMoves];
    const from = _joinWords([...new Set(all.map(m => getCuratedModelName(m.from)))]);
    const laneOf = m => ASSISTANT_LANE_LABELS[m.task] || m.task;
    const movesOf = (list) => {
        const byTarget = new Map();
        for (const m of list) {
            const to = m.to ? getCuratedModelName(m.to) : 'its recommended model';
            if (!byTarget.has(to)) byTarget.set(to, []);
            if (!byTarget.get(to).includes(laneOf(m))) byTarget.get(to).push(laneOf(m));
        }
        return [...byTarget].map(([to, lanes]) => `${_joinWords(lanes)} to ${to}`).join(', ');
    };
    // One clause per distinct flag, so a compaction move gives its own reason.
    const reasons = [...new Set(all.map(m => getAssistantUnusableFlag(m.from, m.task)?.reason || 'Reported unusable in the Assistant.'))];
    const because = _joinWords(reasons.map(reason => reason.charAt(0).toLowerCase() + reason.slice(1).replace(/\.$/, '')));
    const gearNames = [...new Set(gearMoves.map(m => m.gear))];
    const gearsPhrase = `your saved gear${gearNames.length === 1 ? '' : 's'} ${_joinWords(gearNames)}`;
    if (!moved.length) return `Moved ${gearsPhrase} off ${from} on the Assistant (${movesOf(gearMoves)}): it was ${because}.`;
    const gearClause = gearMoves.length
        ? `, and ${gearsPhrase} off it too (${_joinWords([...new Set(gearMoves.map(laneOf))])})`
        : '';
    return `Moved your Assistant off ${from} (${movesOf(moved)})${gearClause}: it was ${because}.`;
}

// ── Saved choices of models that left the pick lists (2026-10-08) ──────────
// SPEC 2026-10-08 D15. A per-task pin, a saved gear, the Assistant quick-pick
// list and the global model all live in this browser's storage, and a pin
// outranks every protected route, so taking a model out of the lists does
// nothing for a device that saved it. This pass moves each saved choice that
// names a model in MODEL_SUCCESSORS to its successor, ONCE per device. The
// marker makes it a migration and not a ban: a pick made afterwards is the
// operator's choice and is kept.
//
// One exception keeps D14: a pin that names the very model its task's route
// runs on (GPT-5.6 Sol on the final quote, Sonnet 5 on the scope proposal)
// is taken out instead of moved, so the task stays on its measured model
// through its route, and follows the route when it moves.
//
// The migration never places a model on a task it is flagged on: a successor
// flagged there (Gemini 3.8 Flash on the Assistant lanes and compaction,
// Fable 5.1 anywhere) takes the pin out, so the task runs on its route. The
// Assistant chat lanes and the quick picks move only to a model checked on
// the Assistant's own request with tools (ASSISTANT_CHECKED_SUCCESSORS).
// With none, the pin is taken out and the quick pick dropped. The 2026-09-29
// Assistant pin migration has run on most devices and will not run again, so
// this pass must not create the pins it removed.
//
// The audio model (qm_audio_model) is not touched: it has its own one-time
// privacy migration (readAudioVideoModelPreference), whose retired Google ids
// must reach it unchanged.
export const MODEL_LIST_MIGRATION_KEY = 'qm_model_list_2026_10_migration_v1';
export const MODEL_LIST_NOTICE_KEY = 'qm_model_list_2026_10_notice_v1';

// The successor of every Anthropic, OpenAI and Google model QuoteMaker has
// listed, routed, priced or named, and that is not in CURRENT_VENDOR_MODELS.
export const MODEL_SUCCESSORS = Object.freeze({
    'anthropic/claude-opus-5': CLAUDE_OPUS,
    'anthropic/claude-opus-4.8': CLAUDE_OPUS,
    'anthropic/claude-opus-4.7': CLAUDE_OPUS,
    'anthropic/claude-opus-4-7': CLAUDE_OPUS,
    'anthropic/claude-opus-4.6': CLAUDE_OPUS,
    'anthropic/claude-opus-4.5': CLAUDE_OPUS,
    'anthropic/claude-sonnet-5': CLAUDE_SONNET_55,
    'anthropic/claude-sonnet-4.6': CLAUDE_SONNET_55,
    'anthropic/claude-sonnet-4.5': CLAUDE_SONNET_55,
    'anthropic/claude-haiku-4.5': CLAUDE_HAIKU_55,
    'anthropic/claude-fable-5': CLAUDE_FABLE_51,
    'openai/gpt-6-sol': GPT61_SOL,
    'openai/gpt-5.6-sol': GPT61_SOL,
    'openai/gpt-5.6-terra': GPT61_SOL,
    'openai/gpt-5.6-luna': GPT6_LUNA,
    'openai/gpt-5.4-mini': GPT6_LUNA,
    'openai/gpt-5.4-nano': GPT6_LUNA,
    'google/gemini-3.7-flash': GEMINI_FLASH,
    'google/gemini-3.6-flash': GEMINI_FLASH,
    'google/gemini-3.5-flash': GEMINI_FLASH,
    'google/gemini-3-flash-preview': GEMINI_FLASH,
    'google/gemini-2.5-flash': GEMINI_FLASH,
    'google/gemini-3.1-flash-lite': GEMINI_LITE,
    [UNAVAILABLE_GEMINI_FLASH_LITE]: GEMINI_LITE,
    'google/gemini-2.5-flash-lite': GEMINI_LITE,
    'google/gemini-3-pro-preview': GEMINI_PRO,
    'google/gemini-2.5-pro': GEMINI_PRO,
});

function _successorOf(modelId) {
    const id = typeof modelId === 'string' ? modelId.trim() : '';
    return id && Object.prototype.hasOwnProperty.call(MODEL_SUCCESSORS, id) ? MODEL_SUCCESSORS[id] : '';
}

// The current models of the three vendors checked on the real Assistant
// request with tools: GPT-6 Luna passed 236 of 264 turns on 2026-09-29 (the
// route table above), and Claude Opus 5.5 completed 10 of 10 live Think
// Harder turns with tools on 2026-10-09. GPT-6.1 Sol, Sonnet 5.5, Haiku 5.5
// and Gemini 3.1 Pro were not measured there, and Gemini 3.8 Flash is
// flagged there.
export const ASSISTANT_CHECKED_SUCCESSORS = Object.freeze([GPT6_LUNA, CLAUDE_OPUS]);

// Where an Assistant choice goes when its general successor was not checked
// there. GPT-5.6 Terra's general successor is GPT-6.1 Sol (its price tier).
// On the Assistant it is GPT-6 Luna, the model the default quick picks put in
// Terra's row.
const ASSISTANT_MODEL_SUCCESSORS = Object.freeze({
    'openai/gpt-5.6-terra': GPT6_LUNA,
});

/**
 * The successor a saved choice on this task moves to, or '' when it has none
 * there. On the Assistant chat lanes only a checked model counts.
 */
function _successorForTask(modelId, task) {
    const successor = _successorOf(modelId);
    if (!successor || !ASSISTANT_CHAT_TASKS.includes(task)) return successor;
    if (ASSISTANT_CHECKED_SUCCESSORS.includes(successor)) return successor;
    const assistantSuccessor = ASSISTANT_MODEL_SUCCESSORS[String(modelId).trim()] || '';
    return ASSISTANT_CHECKED_SUCCESSORS.includes(assistantSuccessor) ? assistantSuccessor : '';
}

/**
 * The quick pick a retired one becomes. The tagline and cost pips describe the
 * model, and the AI Models page cannot edit them, so they come from the
 * successor's default row, never from the retired model's: GPT-5.6 Terra's
 * "Cross-provider specialist" at 2 pips must not label GPT-6 Luna, the
 * cheapest model there. A successor with no default row gets no tagline and
 * no pips rather than the retired model's.
 */
function _quickPickFor(entry, successor) {
    const def = DEFAULT_ASSISTANT_QUICK_MODELS.find(d => d.id === successor);
    if (def) {
        return { ...entry, id: successor, name: def.name, provider: def.provider, tagline: def.tagline, cost: def.cost };
    }
    return {
        ...entry,
        id: successor,
        name: getCuratedModelName(successor).replace(/^Claude\s+/, ''),
        provider: _providerFromModelId(successor),
        tagline: '',
        cost: 0,
    };
}

/**
 * One saved task→model map with each retired model moved. Returns the new map
 * (or null when nothing changed) and the moves made.
 */
function _migrateRetiredPins(stored, label) {
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return { next: null, moves: [] };
    let next = null;
    const moves = [];
    for (const [task, value] of Object.entries(stored)) {
        if (!_successorOf(value)) continue;
        const from = String(value).trim();
        next = next || { ...stored };
        const routeModel = readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[task]) || '';
        if (routeModel === from) {
            delete next[task];
            continue;
        }
        const successor = _successorForTask(from, task);
        if (!successor || getAssistantUnusableFlag(successor, task)) {
            // No usable successor on this task: it runs on its route.
            delete next[task];
            moves.push({ where: label, task, from, to: routeModel || DEFAULT_GENERAL_MODEL });
            continue;
        }
        next[task] = successor;
        moves.push({ where: label, task, from, to: successor });
    }
    return { next, moves };
}

function _readJson(storage, key) {
    try {
        return JSON.parse(storage.getItem(key) || 'null');
    } catch {
        return null;
    }
}

/**
 * Move every saved choice that names a model in MODEL_SUCCESSORS to its
 * successor, once per device (D15): the per-task pins (qm_task_models), each
 * saved gear (qm_model_gear_sets_v1), the Assistant quick picks
 * (qm_assistant_quick_models_v1) and the global model (qm_model). Records a
 * one-line notice for the AI Models page when anything moved. Writes nothing
 * when persistMigration is false or in an eval single-attempt run.
 * @returns {{ moves: Array<{where: string, task?: string, gear?: string, from: string, to: string}>, migrated: boolean }}
 */
export function migrateRetiredModelChoices(storage = _defaultStorage(), { persistMigration = true } = {}) {
    if (!storage) return { moves: [], migrated: false };
    try {
        if (storage.getItem(MODEL_LIST_MIGRATION_KEY) === '1') return { moves: [], migrated: false };
    } catch {
        return { moves: [], migrated: false };
    }
    if (persistMigration === false || isEvalAiSingleAttemptPolicyActive()) return { moves: [], migrated: false };

    const moves = [];
    const writes = [];

    const pins = _migrateRetiredPins(_parseStoredOverrides(storage.getItem(STORAGE_KEY)), 'task');
    moves.push(...pins.moves);
    if (pins.next) writes.push(() => {
        const sanitized = _sanitizeTaskModelOverrides(pins.next);
        if (Object.keys(sanitized).length) storage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
        else if (typeof storage.removeItem === 'function') storage.removeItem(STORAGE_KEY);
    });

    const gears = _readJson(storage, CUSTOM_GEAR_STORAGE_KEY);
    if (Array.isArray(gears)) {
        let gearsChanged = false;
        const nextGears = gears.map((gear, index) => {
            const result = _migrateRetiredPins(gear && typeof gear === 'object' ? gear.overrides : null, 'gear');
            if (!result.next) return gear;
            gearsChanged = true;
            const name = _normalizeCustomGearPreset(gear, index)?.name || `Custom Gear ${index + 1}`;
            moves.push(...result.moves.map(move => ({ ...move, gear: name })));
            return { ...gear, overrides: result.next };
        });
        if (gearsChanged) writes.push(() => storage.setItem(CUSTOM_GEAR_STORAGE_KEY, JSON.stringify(nextGears)));
    }

    const quick = _readJson(storage, ASSISTANT_QUICK_MODELS_KEY);
    if (Array.isArray(quick)) {
        const seen = new Set();
        let quickChanged = false;
        const nextQuick = [];
        for (const entry of quick) {
            const from = entry && typeof entry.id === 'string' ? entry.id.trim() : '';
            const retired = !!_successorOf(from);
            const successor = retired ? _successorForTask(from, 'ai-assistant') : '';
            if (retired && (!successor || getAssistantUnusableFlag(successor, 'ai-assistant'))) {
                // No checked successor on the Assistant: the pick is dropped,
                // never replaced by an unchecked or flagged model.
                quickChanged = true;
                moves.push({ where: 'quick', from, to: '' });
                continue;
            }
            const id = successor || from;
            if (successor) {
                quickChanged = true;
                moves.push({ where: 'quick', from, to: successor });
            }
            if (id && seen.has(id)) {
                quickChanged = true;
                continue;
            }
            if (id) seen.add(id);
            nextQuick.push(successor ? _quickPickFor(entry, successor) : entry);
        }
        if (quickChanged) writes.push(() => storage.setItem(ASSISTANT_QUICK_MODELS_KEY, JSON.stringify(nextQuick)));
    }

    let globalModel = '';
    try { globalModel = String(storage.getItem('qm_model') || '').trim(); } catch { /* unreadable */ }
    const globalSuccessor = _successorOf(globalModel);
    if (globalSuccessor) {
        moves.push({ where: 'global', from: globalModel, to: globalSuccessor });
        writes.push(() => storage.setItem('qm_model', globalSuccessor));
    }

    try {
        for (const write of writes) write();
        if (moves.length) {
            storage.setItem(MODEL_LIST_NOTICE_KEY, JSON.stringify({ moves, at: new Date().toISOString() }));
        }
        storage.setItem(MODEL_LIST_MIGRATION_KEY, '1');
    } catch {
        // Blocked or full storage: the marker is not set either, so the
        // next read tries again.
        return { moves: [], migrated: false };
    }
    if (pins.next) _emitTaskModelOverridesChanged(_sanitizeTaskModelOverrides(pins.next), { source: 'model-list-migration', mode: 'retired_models' });
    if (writes.length) _emitAssistantQuickModelsChanged();
    return { moves, migrated: true };
}

/** The recorded model-list notice, or null when nothing moved or it was dismissed. */
export function getModelListNotice(storage = _defaultStorage()) {
    if (!storage) return null;
    const parsed = _readJson(storage, MODEL_LIST_NOTICE_KEY);
    const moves = Array.isArray(parsed?.moves)
        ? parsed.moves.filter(m => m && typeof m.from === 'string' && typeof m.to === 'string')
        : [];
    return moves.length ? { moves, at: typeof parsed.at === 'string' ? parsed.at : '' } : null;
}

export function dismissModelListNotice(storage = _defaultStorage()) {
    try {
        if (storage && typeof storage.removeItem === 'function') storage.removeItem(MODEL_LIST_NOTICE_KEY);
    } catch { /* best-effort */ }
}

/**
 * One line for the AI Models page naming each move once, in words:
 * "Moved your saved model choices to the current models: Claude Opus 4.8 to
 * Claude Opus 5.5 and Gemini 3.7 Flash to Gemini 3.8 Flash." A quick pick
 * dropped for want of a checked successor (a move with no `to`) is named in a
 * second sentence.
 */
export function formatModelListNotice(notice) {
    const moves = Array.isArray(notice?.moves) ? notice.moves : [];
    if (!moves.length) return '';
    const name = (id) => getCuratedModelName(id);
    const pairs = [...new Set(moves.filter(m => m.to).map(m => `${name(m.from)} to ${name(m.to)}`))];
    const dropped = [...new Set(moves.filter(m => !m.to).map(m => name(m.from)))];
    const sentences = [];
    if (pairs.length) sentences.push(`Moved your saved model choices to the current models: ${_joinWords(pairs)}.`);
    if (dropped.length) sentences.push(`Took ${_joinWords(dropped)} out of the Assistant quick picks, because no current model that replaces ${dropped.length === 1 ? 'it' : 'them'} has passed the Assistant check.`);
    return sentences.join(' ');
}

/**
 * Read the current task→model overrides from localStorage.
 * Returns an object like { 'image-ocr': 'openai/gpt-4o-mini', ... }
 * Runs the one-time Assistant pin migration first, so every reader sees the
 * same state the resolvers route on.
 */
export function getTaskModelOverrides() {
    if (typeof window === 'undefined') return {};
    try {
        return _sanitizeTaskModelOverrides(migrateAssistantUnusablePins().overrides);
    } catch {
        return {};
    }
}

function _emitTaskModelOverridesChanged(overrides = {}, meta = {}) {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    try {
        const detail = {
            overrides,
            source: meta.source || 'model-selector',
            mode: meta.mode || null,
            taskCount: Object.keys(overrides || {}).length,
            timestamp: new Date().toISOString(),
        };
        if (typeof window.CustomEvent === 'function') {
            window.dispatchEvent(new CustomEvent('qm-task-model-overrides-changed', { detail }));
        } else {
            const event = new Event('qm-task-model-overrides-changed');
            event.detail = detail;
            window.dispatchEvent(event);
        }
    } catch { /* notification is best-effort only */ }
}

export function setTaskModelOverrides(nextOverrides = {}, meta = {}) {
    if (typeof window === 'undefined') return {};
    try {
        const sanitized = _sanitizeTaskModelOverrides(nextOverrides);
        if (Object.keys(sanitized).length === 0) {
            localStorage.removeItem(STORAGE_KEY);
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
        }
        _emitTaskModelOverridesChanged(sanitized, meta);
        return sanitized;
    } catch (e) {
        console.warn('[ModelSelector] Failed to persist task overrides:', e?.message);
        return getTaskModelOverrides();
    }
}

export function applyModelGearPreset(preset, meta = {}) {
    const overrides = setTaskModelOverrides(preset?.overrides || {}, {
        source: meta.source || 'gear-preset',
        mode: meta.mode || 'apply_gear',
    });
    if (typeof window !== 'undefined' && typeof preset?.embeddingModel === 'string' && preset.embeddingModel.trim()) {
        try {
            localStorage.setItem('qm_embedding_model', preset.embeddingModel.trim());
        } catch (e) {
            console.warn('[ModelSelector] Failed to persist gear embedding model:', e?.message);
        }
    }
    return overrides;
}

export function getBuiltInModelGearPresets() {
    return [...MODEL_GEAR_PRESETS, RJ_BIRD_PRIVACY_SAFE_GEAR].map(_cloneGearPreset);
}

export function getCustomModelGearPresets() {
    if (typeof window === 'undefined') return [];
    // The one-time Assistant pin migration also cleans saved gears, so a gear
    // read before anything reads the live overrides is migrated too.
    try { migrateAssistantUnusablePins(); } catch { /* storage unavailable */ }
    try {
        const raw = localStorage.getItem(CUSTOM_GEAR_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((preset, index) => _normalizeCustomGearPreset(preset, index))
            .filter(Boolean);
    } catch {
        return [];
    }
}

export function saveCustomModelGearPreset(name, overrides = getTaskModelOverrides()) {
    if (typeof window === 'undefined') return null;
    try {
        const safeOverrides = _sanitizeTaskModelOverrides(overrides);
        const trimmedName = typeof name === 'string' && name.trim()
            ? name.trim()
            : `Custom Gear ${new Date().toLocaleDateString()}`;
        const preset = _normalizeCustomGearPreset({
            id: `custom-${Date.now()}`,
            name: trimmedName,
            shortLabel: trimmedName.slice(0, 12),
            createdAt: new Date().toISOString(),
            overrides: safeOverrides,
        });
        const next = [
            preset,
            ...getCustomModelGearPresets().filter(existing => existing.id !== preset.id),
        ].slice(0, 12);
        localStorage.setItem(CUSTOM_GEAR_STORAGE_KEY, JSON.stringify(next));
        return preset;
    } catch (e) {
        console.warn('[ModelSelector] Failed to save custom model gear:', e?.message);
        return null;
    }
}

export function deleteCustomModelGearPreset(id) {
    if (typeof window === 'undefined') return [];
    try {
        const next = getCustomModelGearPresets().filter(preset => preset.id !== id);
        localStorage.setItem(CUSTOM_GEAR_STORAGE_KEY, JSON.stringify(next));
        return next;
    } catch (e) {
        console.warn('[ModelSelector] Failed to delete custom model gear:', e?.message);
        return getCustomModelGearPresets();
    }
}

// ── QM Assistant quick-pick models ───────────────────────────────────────────
// The model dropdown in the QM Assistant header (ModelSwitcher.jsx) shows a
// curated SHORTLIST so the user can switch the assistant's model in one click.
// That list used to be hardcoded inside the component (and went stale — it even
// listed a nonexistent "Opus 4.8"). It now lives here, is user-editable from the
// AI Models page, persists to localStorage, and defaults to the curated
// catalogue so it never drifts. Selecting a quick pick still routes through
// setTaskModelOverride('ai-assistant', id) unchanged — this list ONLY controls
// which models appear as one-click options.
const ASSISTANT_QUICK_MODELS_KEY = 'qm_assistant_quick_models_v1';
const ASSISTANT_QUICK_MODELS_KILL = 'qm_disable_assistant_quick_models';

// `cost` is a relative 1-4 rank for the dropdown's cost meter, not a real price.
// Gemini ids mirror SettingsModal CURATED_MODELS (the validated Settings
// catalogue), NOT the 3.1 gear-preset constants above; the Anthropic ids reuse
// the constants, which already match the catalogue.
// Refreshed 2026-08-07. Every entry is tool-call verified against QuoteMaker's
// real 156-tool payload, because this list drives the Assistant model switcher
// and a model that cannot hold a tool call is useless here regardless of price.
// gpt-5.4-nano is deliberately ABSENT: it is the cheapest classifier in the
// pipeline but was never verified on tool calling, so it must not be one click
// away from the agent loop.
// 2026-09-29: the first three are the measured Assistant route and its
// fallbacks. Gemini 3.8 Flash left the default list: a Customer Zero user
// reported it unusable in the Assistant (ASSISTANT_UNUSABLE_MODELS). A saved
// list that still holds it keeps it, and the switcher shows the flag and the
// warning on that row. Grok 4.7 replaced Grok 4.6 because 4.7 is the one
// measured on the Assistant request.
// 2026-10-08 (SPEC 2026-10-08 D13): Opus 4.8 and GPT-5.6 Terra left the pick
// lists. Opus 5.5 takes the Opus row. GPT-6 Luna takes Terra's: on the same
// 2026-09-29 Assistant request it passed 236 of 264 turns (89.4%) with no
// failed call, where Terra passed 116 of 132 (87.9%).
export const DEFAULT_ASSISTANT_QUICK_MODELS = [
    { id: ASSISTANT_MODEL, provider: 'other',     name: 'DeepSeek V4.1 Flash',  tagline: 'Measured best Assistant model',      cost: 1, recommended: true },
    { id: QWEN_MAX,      provider: 'other',     name: 'Qwen3.8 Max',          tagline: 'Measured fallback, careful and slower', cost: 2 },
    { id: GROK_47,       provider: 'other',     name: 'Grok 4.7',             tagline: 'Measured fallback, fast',            cost: 3 },
    { id: GPT6_LUNA,     provider: 'other',     name: 'GPT-6 Luna',           tagline: 'Measured on the Assistant, cheapest', cost: 1 },
    { id: CLAUDE_OPUS,   provider: 'anthropic', name: 'Opus 5.5',             tagline: 'Deepest reasoning, Think Harder',    cost: 4 },
    { id: GLM_53,        provider: 'other',     name: 'GLM 5.3 Max',          tagline: 'Optional operator-selected model',    cost: 2 },
    { id: GEMINI_LITE,   provider: 'google',    name: 'Gemini 3.5 Flash-Lite', tagline: 'Fast, cheap, multimodal',           cost: 1 },
];

function _providerFromModelId(id) {
    const s = String(id || '').toLowerCase();
    if (s.includes('anthropic') || s.includes('claude')) return 'anthropic';
    if (s.includes('google') || s.includes('gemini')) return 'google';
    return 'other';
}

// "Recommended" marks the model the Assistant runs on by default, so it is
// worked out from the current ai-assistant recommendation every time the list
// is read or saved; a stored value is ignored. A list saved before 2026-09-29
// carried recommended:true on Gemini 3.8 Flash, which a Customer Zero user
// reported unusable in the Assistant, and trusting it put "Recommended" next
// to that flag.
function _sanitizeQuickModels(list) {
    if (!Array.isArray(list)) return null;
    const recommendedId = readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS['ai-assistant']);
    const out = [];
    const seen = new Set();
    for (const m of list) {
        if (!m || typeof m !== 'object') continue;
        const id = typeof m.id === 'string' ? m.id.trim() : '';
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const provider = ['anthropic', 'google', 'other'].includes(m.provider) ? m.provider : _providerFromModelId(id);
        const costNum = Number(m.cost);
        out.push({
            id,
            name: (typeof m.name === 'string' && m.name.trim()) ? m.name.trim() : id,
            provider,
            tagline: typeof m.tagline === 'string' ? m.tagline : '',
            cost: Number.isFinite(costNum) ? Math.max(0, Math.min(4, Math.round(costNum))) : 0,
            recommended: !!recommendedId && id === recommendedId,
        });
    }
    return out.length ? out : null;
}

function _assistantQuickModelsDisabled() {
    if (typeof window === 'undefined') return false;
    try {
        if (window.qm_disable_assistant_quick_models === true) return true;
        if (localStorage.getItem(ASSISTANT_QUICK_MODELS_KILL) === 'true') return true;
    } catch { /* noop */ }
    return false;
}

function _emitAssistantQuickModelsChanged() {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    try {
        if (typeof window.CustomEvent === 'function') {
            window.dispatchEvent(new CustomEvent('qm-assistant-quick-models-changed'));
        } else {
            window.dispatchEvent(new Event('qm-assistant-quick-models-changed'));
        }
    } catch { /* best-effort */ }
}

/** The curated default quick-pick list (fresh clones, never the shared objects). */
export function getDefaultAssistantQuickModels() {
    return DEFAULT_ASSISTANT_QUICK_MODELS.map(m => ({ ...m }));
}

/**
 * The QM Assistant's quick-pick model list. Returns the user's saved list, or the
 * curated default when nothing is saved (or when the kill switch is set).
 */
export function getAssistantQuickModels() {
    if (typeof window === 'undefined' || _assistantQuickModelsDisabled()) {
        return getDefaultAssistantQuickModels();
    }
    try {
        const raw = localStorage.getItem(ASSISTANT_QUICK_MODELS_KEY);
        const parsed = raw ? _sanitizeQuickModels(JSON.parse(raw)) : null;
        return parsed || getDefaultAssistantQuickModels();
    } catch {
        return getDefaultAssistantQuickModels();
    }
}

/** Persist the quick-pick list. Pass null/empty to clear back to the default. */
export function setAssistantQuickModels(list) {
    if (typeof window === 'undefined') return getAssistantQuickModels();
    try {
        const sanitized = _sanitizeQuickModels(list);
        if (!sanitized) {
            localStorage.removeItem(ASSISTANT_QUICK_MODELS_KEY);
        } else {
            localStorage.setItem(ASSISTANT_QUICK_MODELS_KEY, JSON.stringify(sanitized));
        }
        _emitAssistantQuickModelsChanged();
        return getAssistantQuickModels();
    } catch (e) {
        console.warn('[ModelSelector] Failed to persist assistant quick models:', e?.message);
        return getAssistantQuickModels();
    }
}

/** Clear any customization and return to the curated default. */
export function resetAssistantQuickModels() {
    if (typeof window !== 'undefined') {
        try { localStorage.removeItem(ASSISTANT_QUICK_MODELS_KEY); } catch { /* noop */ }
        _emitAssistantQuickModelsChanged();
    }
    return getDefaultAssistantQuickModels();
}

/**
 * Set a per-task model override.
 * Pass an empty/null modelId to clear the override (the task will fall back to the global model).
 */
export function setTaskModelOverride(taskName, modelId) {
    if (typeof window === 'undefined') return;
    try {
        const overrides = getTaskModelOverrides();
        if (!modelId || !modelId.trim()) {
            delete overrides[taskName];
        } else {
            overrides[taskName] = modelId.trim();
        }
        setTaskModelOverrides(overrides);
    } catch (e) {
        console.warn('[ModelSelector] Failed to persist task override:', e?.message);
    }
}

/**
 * Clear ALL per-task overrides.
 */
export function clearAllTaskModelOverrides(meta = {}) {
    if (typeof window === 'undefined') return;
    try {
        localStorage.removeItem(STORAGE_KEY);
        _emitTaskModelOverridesChanged({}, {
            source: meta.source || 'model-selector',
            mode: meta.mode || 'clear_all',
        });
    } catch (e) {
        console.warn('[ModelSelector] Failed to clear overrides:', e?.message);
    }
}

/**
 * Pick the right model for a given task name.
 *
 * Priority order (highest wins):
 *   1. window.QM_MODEL_OVERRIDES[taskName]    - console-only override (dev/testing)
 *   2. localStorage qm_task_models[taskName]  - UI-set per-task override
 *   3. defaultModel argument                  - user's global model from Settings
 *   4. TASK_MODEL_RECOMMENDATIONS[taskName]   - last-resort suggestion
 *
 * @param {string} taskName     - The task identifier
 * @param {string} defaultModel - Fallback model (the user's global model from props)
 * @returns {string} The model identifier in OpenRouter format
 */
// Session-scoped dedup so the UI's per-row pickModelForTask probes don't
// spam the console. Each (taskName|sourceLabel|candidate) triplet warns once
// per page load, then silently redirects.
const _audioWarnCache = new Set();

function _readTaskModelOverridesFromStorage({ persistMigration = true } = {}) {
    if (typeof window === 'undefined') return {};
    try {
        return migrateAssistantUnusablePins(undefined, { persistMigration }).overrides || {};
    } catch {
        return {};
    }
}

function _readLocalStorageModel(key) {
    if (typeof window === 'undefined') return '';
    try {
        const value = localStorage.getItem(key);
        return value && value.trim() ? value : '';
    } catch {
        return '';
    }
}

/**
 * Resolve a task route with an explicit audit object. This mirrors the
 * pickModelForTask precedence but exposes why the effective model was chosen,
 * whether a protected recommendation was bypassed, and whether an audio/video
 * capability reroute happened.
 */
export function resolveModelRouteForTask(taskName, defaultModel) {
    const recEntry = TASK_MODEL_RECOMMENDATIONS[taskName];
    const recommendationModel = readModelFromRecommendation(recEntry);
    const strongRecommendation = !!(recEntry && typeof recEntry === 'object' && recEntry.strongRecommendation === true);
    const strongReason = strongRecommendation && typeof recEntry.reason === 'string' ? recEntry.reason : '';

    const base = {
        taskName,
        requestedModel: '',
        requestedSource: 'none',
        effectiveModel: '',
        model: '',
        routeSource: 'none',
        recommendationModel: recommendationModel || null,
        strongRecommendation,
        strongReason,
        strongRecommendationBypassed: false,
        overrideApplied: false,
        availabilityRerouted: false,
        availabilityWarnings: [],
        capabilityRerouted: false,
        capabilityWarnings: [],
        failClosed: false,
    };

    const finish = (candidate, source, opts = {}) => {
        const requested = candidate && String(candidate).trim() ? String(candidate).trim() : '';
        const availability = resolveUnavailableModelLane(taskName, requested);
        let effective = availability.effectiveModel;
        const warnings = [];
        if (AUDIO_VIDEO_TASKS.has(taskName) && !isAudioCapableModel(effective)) {
            const fallback = recommendationModel || AUDIO_VIDEO_FALLBACK_MODEL;
            if (requested) {
                warnings.push(`${source} "${requested}" cannot process audio/video input; routed to "${fallback}".`);
            } else {
                warnings.push(`No audio/video-capable ${source} was available; routed to "${fallback}".`);
            }
            effective = fallback;
        } else if (VISION_TASKS.has(taskName) && !isVisionCapableModel(effective)) {
            const fallback = recommendationModel || MEDIA_FALLBACK_MODEL;
            if (requested) {
                warnings.push(`${source} "${requested}" cannot process vision input; routed to "${fallback}".`);
            } else {
                warnings.push(`No vision-capable ${source} was available; routed to "${fallback}".`);
            }
            effective = fallback;
        }
        const bypassed = !!opts.overrideApplied
            && strongRecommendation
            && !!recommendationModel
            && !!requested
            && requested !== recommendationModel;
        return {
            ...base,
            requestedModel: requested,
            requestedSource: source,
            effectiveModel: effective,
            model: effective,
            routeSource: availability.availabilityRerouted
                ? 'availability lane'
                : (opts.routeSource || source),
            strongRecommendationBypassed: bypassed,
            overrideApplied: !!opts.overrideApplied,
            availabilityRerouted: availability.availabilityRerouted,
            availabilityWarnings: availability.availabilityWarning
                ? [availability.availabilityWarning]
                : [],
            capabilityRerouted: warnings.length > 0,
            capabilityWarnings: warnings,
            failClosed: false,
        };
    };

    if (typeof window !== 'undefined' && window.QM_MODEL_OVERRIDES?.[taskName]) {
        return finish(window.QM_MODEL_OVERRIDES[taskName], 'console override', { overrideApplied: true });
    }

    const overrides = _readTaskModelOverridesFromStorage();
    if (overrides?.[taskName]) {
        return finish(overrides[taskName], 'per-task override', { overrideApplied: true });
    }

    // The dedicated Settings -> Audio/Video Model is an intentional modality
    // choice, not the general text global. Honour it before the protected media
    // default, while still applying the fail-closed capability check in finish().
    if (AUDIO_VIDEO_TASKS.has(taskName)) {
        const audioFromStorage = readAudioVideoModelPreference();
        if (audioFromStorage) return finish(audioFromStorage, 'audio global (localStorage)');
    }

    if (strongRecommendation && recommendationModel) {
        return finish(recommendationModel, 'protected recommendation');
    }

    if (MEDIA_TASKS.has(taskName)) {
        const rec = recommendationModel || MEDIA_FALLBACK_MODEL;
        const mediaModel = AUDIO_VIDEO_TASKS.has(taskName) && !isAudioCapableModel(rec)
            ? AUDIO_VIDEO_FALLBACK_MODEL
            : rec;
        return finish(mediaModel, 'media task recommendation');
    }

    if (defaultModel && defaultModel.trim()) return finish(defaultModel, 'global model');

    const globalFromStorage = _readLocalStorageModel('qm_model');
    if (globalFromStorage) return finish(globalFromStorage, 'global model (localStorage)');

    if (MEDIA_TASKS.has(taskName)) {
        const rec = recommendationModel || MEDIA_FALLBACK_MODEL;
        const mediaModel = AUDIO_VIDEO_TASKS.has(taskName) && !isAudioCapableModel(rec)
            ? AUDIO_VIDEO_FALLBACK_MODEL
            : rec;
        return finish(mediaModel, 'media fallback');
    }

    if (recommendationModel) return finish(recommendationModel, 'task recommendation');
    return finish(DEFAULT_GENERAL_MODEL, 'default general model');
}

export function pickModelForTask(taskName, defaultModel, {
    persistPreferenceMigrations = true,
} = {}) {
    // Helper - modality tasks redirect to a capable fallback if the resolved
    // model cannot process the input shape. Explicit per-task overrides still
    // record bypass intent, but unsupported modality picks fail closed to the
    // tuned route rather than breaking the import/analysis pipeline.
    const ensureTaskCapabilities = (candidate, sourceLabel) => {
        const availability = resolveUnavailableModelLane(taskName, candidate);
        if (availability.availabilityRerouted) {
            const cacheKey = `${taskName}|availability|${availability.requestedModel}`;
            if (!_audioWarnCache.has(cacheKey)) {
                _audioWarnCache.add(cacheKey);
                console.warn(`[ModelSelector] ${availability.availabilityWarning}`);
            }
        }
        candidate = availability.effectiveModel;
        if (AUDIO_VIDEO_TASKS.has(taskName) && !isAudioCapableModel(candidate)) {
            const fallback = readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[taskName]) || AUDIO_VIDEO_FALLBACK_MODEL;
            if (candidate) {
                const cacheKey = `${taskName}|${sourceLabel}|${candidate}`;
                if (!_audioWarnCache.has(cacheKey)) {
                    _audioWarnCache.add(cacheKey);
                    console.warn(`[ModelSelector] ${taskName}: ${sourceLabel} "${candidate}" can't process audio - routing to "${fallback}" instead. Set an audio-capable per-task override (Gemini / GPT Audio / Voxtral) in AI Models to silence this.`);
                }
            }
            return fallback;
        }
        if (VISION_TASKS.has(taskName) && !isVisionCapableModel(candidate)) {
            const fallback = readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[taskName]) || MEDIA_FALLBACK_MODEL;
            if (candidate) {
                const cacheKey = `${taskName}|${sourceLabel}|${candidate}`;
                if (!_audioWarnCache.has(cacheKey)) {
                    _audioWarnCache.add(cacheKey);
                    console.warn(`[ModelSelector] ${taskName}: ${sourceLabel} "${candidate}" can't process vision input - routing to "${fallback}" instead. Set a vision-capable per-task override to silence this.`);
                }
            }
            return fallback;
        }
        return candidate;
    };

    // Priority 1: console runtime override (for dev/testing)
    if (typeof window !== 'undefined' && window.QM_MODEL_OVERRIDES?.[taskName]) {
        return ensureTaskCapabilities(window.QM_MODEL_OVERRIDES[taskName], 'console override');
    }

    // Priority 2: UI-set per-task override from localStorage. The read runs
    // the one-time Assistant pin migration first, so a stored pin of a model
    // flagged unusable in the Assistant never routes an Assistant lane.
    if (typeof window !== 'undefined') {
        const overrides = _readTaskModelOverridesFromStorage({ persistMigration: persistPreferenceMigrations });
        if (overrides?.[taskName]) return ensureTaskCapabilities(overrides[taskName], 'per-task override');
    }

    // Priority 2.5: the dedicated audio/video global. This used to be read
    // after protected/media recommendations, which made the Settings control
    // ineffective. It now wins unless a per-task/console override exists.
    if (AUDIO_VIDEO_TASKS.has(taskName) && typeof window !== 'undefined') {
        const audioFromStorage = readAudioVideoModelPreference(undefined, {
            persistMigration: persistPreferenceMigrations,
        });
        if (audioFromStorage) {
            return ensureTaskCapabilities(audioFromStorage, 'audio global (localStorage)');
        }
    }

    // Priority 2.75 (v5.11.31, 2026-04-22): ENFORCED strong recommendation.
    // Peter 2026-04-22: "Protected" tasks in the AI Models page (the
    // strongRecommendation list - e.g. ai-assistant → Sonnet 4.6,
    // ai-assistant-deep → Opus) were previously ADVISORY. The global
    // model still won over them at Priority 3, which meant a Gemini-class
    // global silently pulled `ai-assistant` to Gemini despite the "PROTECTED"
    // UI badge. That badge now matches reality: if strongRecommendation=true
    // AND the user has NOT set an explicit per-task override (Priority 2),
    // the recommended model wins over the global. The explicit "OVERRIDE
    // ANYWAY" button in AI Models writes a Priority 2 override, which still
    // wins — so the escape hatch is preserved for users who really mean it.
    const recEntry = TASK_MODEL_RECOMMENDATIONS[taskName];
    if (recEntry && typeof recEntry === 'object' && recEntry.strongRecommendation === true) {
        const recModel = readModelFromRecommendation(recEntry);
        if (recModel) return ensureTaskCapabilities(recModel, 'protected recommendation');
    }

    // Priority 2.8: media-safe task recommendation. Vision/audio/video calls
    // cannot blindly inherit a text/general global; Kimi/Sonnet-style globals
    // can be fine for prose but wrong for photo analysis, OCR, plans, or audio.
    // Explicit per-task overrides above still win for power users.
    if (MEDIA_TASKS.has(taskName)) {
        const rec = readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[taskName]) || MEDIA_FALLBACK_MODEL;
        const mediaModel = AUDIO_VIDEO_TASKS.has(taskName) && !isAudioCapableModel(rec)
            ? AUDIO_VIDEO_FALLBACK_MODEL
            : rec;
        return ensureTaskCapabilities(mediaModel, 'media task recommendation');
    }

    // Priority 3: user's global model from Settings (threaded through as
    // `defaultModel` by the call site - authoritative when present).
    if (defaultModel && defaultModel.trim()) return ensureTaskCapabilities(defaultModel, 'global model');

    // Priority 3.5: localStorage backstop for the global model. SettingsModal
    // persists the selected global to `qm_model` on save. Reading it here
    // guarantees the user's global wins even for services whose callers
    // forgot to thread `model` through. Without this, a `pickModelForTask`
    // call from deep in a service chain where the global wasn't passed would
    // silently fall to the recommendation - the "hardcoded" symptom the user
    // sees. Keep this AFTER priority 3 so explicit overrides still win.
    if (typeof window !== 'undefined') {
        try {
            const globalFromStorage = localStorage.getItem('qm_model');
            if (globalFromStorage && globalFromStorage.trim()) return ensureTaskCapabilities(globalFromStorage, 'global model (localStorage)');
        } catch { /* fall through */ }
    }

    // Priority 4: media-safe fallback - only when no global is resolvable AND
    // the task sends images/audio/video. Keeps the import pipeline working
    // for brand-new installs where the user hasn't picked a global yet.
    if (MEDIA_TASKS.has(taskName)) {
        const rec = readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[taskName]) || MEDIA_FALLBACK_MODEL;
        return AUDIO_VIDEO_TASKS.has(taskName) && !isAudioCapableModel(rec)
            ? AUDIO_VIDEO_FALLBACK_MODEL
            : rec;
    }

    // Priority 5: tier recommendation - last resort when no global is set
    return readModelFromRecommendation(TASK_MODEL_RECOMMENDATIONS[taskName]) || DEFAULT_GENERAL_MODEL;
}

/**
 * List of all tasks the AI Models UI should expose.
 * Combines the recommendation map + metadata keys for completeness.
 */
export function listAllTasks() {
    const taskNames = Array.from(new Set([
        ...Object.keys(TASK_MODEL_RECOMMENDATIONS),
        ...Object.keys(TASK_METADATA),
    ]));
    return taskNames.map(name => {
        const entry = TASK_MODEL_RECOMMENDATIONS[name];
        const strong = !!(entry && typeof entry === 'object' && entry.strongRecommendation === true);
        return {
            name,
            label: TASK_METADATA[name]?.label || name,
            description: TASK_METADATA[name]?.description || '',
            recommendation: readModelFromRecommendation(entry) || null,
            costTier: TASK_METADATA[name]?.costTier || 'medium',
            strongRecommendation: strong,
            strongReason: strong && typeof entry.reason === 'string' ? entry.reason : '',
        };
    });
}

// ── Older models of the three vendors in the live catalogue (2026-10-08) ────
// SPEC 2026-10-08 D13 / I8. The curated lists name the current Anthropic,
// OpenAI and Google models (CURRENT_VENDOR_MODELS), but the choosers also
// search the live OpenRouter catalogue, which still lists every older build
// (Opus 4.8, Sonnet 4.6, GPT-5.x, Gemini 2.5 ...). Those are hidden there too,
// unless a lane routes to one now: a measured route that has not moved yet
// (GPT-5.6 Sol on the final quote) stays pickable, so an operator who tried
// another model can put the lane back.

// Only the vendors' general chat lines are compared: Claude Opus, Sonnet,
// Haiku and Fable, GPT and the o-series, Gemini Flash, Flash-Lite and Pro. An
// id outside them (GPT Audio, Whisper, Gemma, gpt-oss, an embedding, TTS or
// image model) is not an older chat model and stays offered: the audio lanes
// need GPT Audio and Whisper. A model newer than the current list (Opus 5.6,
// Gemini 3.9 Flash) stays offered too, so a new release is pickable before
// the list is updated.
const NON_CHAT_LINE = /(?:^|-)(?:audio|transcribe|tts|realtime|embedding|image)(?:-|$)/;
const CHAT_LINE_VERSION = '(\\d+(?:[.-]\\d{1,2})?)';
const CHAT_LINES = Object.freeze({
    anthropic: [
        new RegExp(`^claude-(opus|sonnet|haiku|fable)-${CHAT_LINE_VERSION}(?=-|$)(.*)$`),
        new RegExp(`^claude-${CHAT_LINE_VERSION}-(opus|sonnet|haiku|fable)(?=-|$)(.*)$`),
        new RegExp(`^claude-${CHAT_LINE_VERSION}()(?=-|$)(.*)$`),
    ],
    openai: [
        /^(?:chat)?gpt-(\d+(?:\.\d+)?)(o?)(?=-|$)(.*)$/,
        /^o(\d+)()(?=-|$)(.*)$/,
    ],
    google: [
        new RegExp(`^gemini-${CHAT_LINE_VERSION}-(flash-lite|flash|pro)(?=-|$)(.*)$`),
        new RegExp(`^gemini-(flash-lite|flash|pro)-${CHAT_LINE_VERSION}(?=-|$)(.*)$`),
        new RegExp(`^gemini-${CHAT_LINE_VERSION}()(?=-|$)(.*)$`),
    ],
});

function _versionOf(text) {
    const [major, minor] = String(text).split(/[.-]/).map(Number);
    return [major || 0, minor || 0];
}

function _compareVersions(a, b) {
    return a[0] - b[0] || a[1] - b[1];
}

/**
 * The chat line of a model id (no vendor prefix), or null when it is not one.
 * `family` is the line (opus, flash-lite, sol ...), '' for a bare version.
 */
function _chatLineOf(vendor, model) {
    for (const pattern of CHAT_LINES[vendor] || []) {
        const match = model.match(pattern);
        if (!match) continue;
        const [, a, b, rest] = match;
        if (vendor === 'openai') {
            // gpt-6.1-sol-pro: version 6.1, line "sol", rest "-sol-pro".
            // gpt-4o: version 4, line "o". o3-mini: the o-series, version 3.
            const tier = rest.split('-')[1] || '';
            const family = pattern.source.startsWith('^o') ? 'o-series' : `${b}${tier}`;
            return { family, version: _versionOf(a), rest };
        }
        // The version is whichever capture holds digits.
        const versionFirst = /^\d/.test(a);
        return { family: versionFirst ? b : a, version: _versionOf(versionFirst ? a : b), rest };
    }
    return null;
}

let _currentLinesCache = null;
function _currentLines() {
    if (_currentLinesCache) return _currentLinesCache;
    const newest = new Map();
    const lowest = new Map();
    for (const id of CURRENT_VENDOR_MODELS) {
        const [vendor, model] = id.split('/');
        const line = _chatLineOf(vendor, model);
        if (!line) continue;
        const key = `${vendor}/${line.family}`;
        if (!newest.has(key) || _compareVersions(line.version, newest.get(key)) > 0) newest.set(key, line.version);
        if (!lowest.has(vendor) || _compareVersions(line.version, lowest.get(vendor)) < 0) lowest.set(vendor, line.version);
    }
    _currentLinesCache = { newest, lowest };
    return _currentLinesCache;
}

/**
 * True for an older chat model of one of the three vendors: one whose line
 * has a newer model in the current list (Opus 4.8 against Opus 5.5), or, for
 * a line with no current model (GPT-5.6 Terra, GPT-4o, the o-series), one
 * older than the vendor's oldest current generation. A variant of a current
 * version that is not itself listed (GPT-6.1 Sol Pro) counts as older. A
 * `:variant` suffix (`:batch`, `:free`) reads as its base model. Every other
 * vendor, and every id outside the chat lines, returns false.
 */
export function isSupersededVendorModel(modelId) {
    const id = String(modelId || '').trim().toLowerCase().split(':')[0];
    const slash = id.indexOf('/');
    if (slash < 1) return false;
    const vendor = id.slice(0, slash);
    if (!CURRENT_MODEL_VENDORS.includes(vendor) || CURRENT_VENDOR_MODELS.includes(id)) return false;
    const line = _chatLineOf(vendor, id.slice(slash + 1));
    if (!line || NON_CHAT_LINE.test(line.rest)) return false;
    const { newest, lowest } = _currentLines();
    const current = newest.get(`${vendor}/${line.family}`);
    if (current) return _compareVersions(line.version, current) <= 0;
    const oldest = lowest.get(vendor);
    return !!oldest && _compareVersions(line.version, oldest) < 0;
}

/**
 * Every model a lane uses now: each task's recommended route and the model it
 * resolves to on this device (its pin, the audio choice or the global).
 * @param {string} [globalModel] - the Settings global, when the caller has it
 * @returns {Set<string>}
 */
export function getRoutedModelIds(globalModel) {
    const routed = new Set();
    for (const { name, recommendation } of listAllTasks()) {
        if (recommendation) routed.add(recommendation);
        try {
            const effective = resolveModelRouteForTask(name, globalModel).effectiveModel;
            if (effective) routed.add(effective);
        } catch { /* a task that cannot resolve adds nothing */ }
    }
    return routed;
}

/**
 * The catalogue rows a chooser may offer: every row except an older model of
 * the three vendors that no lane uses now. Rows are `{ id, ... }`.
 * @param {Array<{id: string}>} models
 * @param {{ globalModel?: string, routed?: Set<string> }} [options]
 */
export function filterPickableModels(models, { globalModel, routed } = {}) {
    const list = Array.isArray(models) ? models : [];
    let inUse = routed || null;
    return list.filter((model) => {
        const id = model && typeof model.id === 'string' ? model.id : '';
        if (!isSupersededVendorModel(id) && !isImageGeneratorOfCurrentVendor(id, model?.modality)) return true;
        inUse = inUse || getRoutedModelIds(globalModel);
        return inUse.has(id);
    });
}

// An image generator of the three vendors ("text+image->text+image"). No
// QuoteMaker task makes images, so the choosers never offer one unless a lane
// uses it now (live drill 2026-10-09: gpt-5-image, gemini-2.5-flash-image and
// three more still showed in the per-task chooser).
function isImageGeneratorOfCurrentVendor(modelId, modality) {
    const id = String(modelId || '').trim().toLowerCase();
    const vendor = id.slice(0, Math.max(0, id.indexOf('/')));
    if (!CURRENT_MODEL_VENDORS.includes(vendor)) return false;
    const output = String(modality || '').toLowerCase().split('->')[1] || '';
    return /\bimage\b/.test(output);
}

export default {
    TASK_MODEL_MAP,
    TASK_MODEL_RECOMMENDATIONS,
    TASK_METADATA,
    DOCUMENT_CLASSIFICATION_MODEL_TASK,
    MODEL_GEAR_PRESETS,
    RJ_BIRD_PRIVACY_SAFE_GEAR,
    RJ_BIRD_PRIVACY_SAFE_OVERRIDES,
    MEDIA_TASKS,
    VISION_TASKS,
    AUDIO_VIDEO_TASKS,
    pickModelForTask,
    resolveModelRouteForTask,
    isVisionCapableModel,
    isAudioCapableModel,
    getTaskModelOverrides,
    setTaskModelOverrides,
    setTaskModelOverride,
    clearAllTaskModelOverrides,
    applyModelGearPreset,
    getBuiltInModelGearPresets,
    getCustomModelGearPresets,
    saveCustomModelGearPreset,
    deleteCustomModelGearPreset,
    listAllTasks,
    readModelFromRecommendation,
    resolveUnavailableModelLane,
    isUnavailableOpenRouterModel,
    getRecommendationEntry,
    getStrongRecommendations,
    isStrongRecommendation,
    getStrongRecommendationReason,
    detectGlobalBelowRecommendedTier,
    getAssistantFallbackModels,
    getAssistantUnusableFlag,
    isAssistantUnusableModel,
    migrateAssistantUnusablePins,
    getAssistantRouteNotice,
    dismissAssistantRouteNotice,
    formatAssistantRouteNotice,
    isSupersededVendorModel,
    getRoutedModelIds,
    filterPickableModels,
    migrateRetiredModelChoices,
    getModelListNotice,
    dismissModelListNotice,
    formatModelListNotice,
};
